import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { useAuth } from '../context/AuthContext';
import ExportButton from '../components/common/ExportButton';
import { INDIAN_STATES, COUNTRIES, MAJOR_CITIES } from '../config/geoData';
import CareerTimeline from '../components/hr/CareerTimeline';

/* ── Helpers ──────────────────────────────────────────────────── */
const fmt = (v) => v || '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtSalary = (n, cur = 'INR') => {
  if (!n) return '—';
  const v = parseFloat(n);
  const sym = cur === 'INR' ? '₹' : '$';
  if (v >= 100000) return `${sym}${(v/100000).toFixed(1)}L/yr`;
  if (v >= 1000)   return `${sym}${(v/1000).toFixed(0)}K/yr`;
  return `${sym}${v}/yr`;
};

const STATUS_META = {
  active:      { label: 'Active',      bg: '#d1fae5', color: '#065f46' },
  on_leave:    { label: 'On Leave',    bg: '#fef3c7', color: '#92400e' },
  on_notice:   { label: 'On Notice',   bg: '#ffedd5', color: '#c2410c' },
  resigned:    { label: 'Resigned',    bg: '#fee2e2', color: '#991b1b' },
  terminated:  { label: 'Terminated',  bg: '#fce7f3', color: '#9d174d' },
};
const EMP_TYPE_META = {
  full_time: { label: 'Full Time',  bg: '#dbeafe', color: '#1e40af' },
  part_time: { label: 'Part Time',  bg: '#ede9fe', color: '#5b21b6' },
  contract:  { label: 'Contract',   bg: '#fef9c3', color: '#854d0e' },
  intern:    { label: 'Intern',     bg: '#f0fdf4', color: '#166534' },
};
const LEAVE_STATUS = {
  pending:  { label: 'Pending',  bg: '#fef3c7', color: '#92400e' },
  approved: { label: 'Approved', bg: '#d1fae5', color: '#065f46' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1b' },
};
const LEAVE_TYPES = ['sick','casual','earned','maternity','paternity','unpaid','comp_off'];
const DEPARTMENTS = ['Research & Development','Quality Control','Quality Assurance','Regulatory Affairs',
  'Sales & Marketing','Business Development','Finance','Human Resources','Operations','IT','Administration','Logistics'];

const DESIGNATIONS = [
  // Leadership
  'CEO', 'COO', 'Director', 'General Manager',
  // R&D / Science
  'R&D Head', 'Principal Scientist', 'Senior Scientist', 'Scientist',
  'Research Associate', 'Senior Research Associate', 'Lab Technician',
  // Quality & Regulatory
  'QC Manager', 'QA Manager', 'QC Analyst', 'QA Executive', 'Regulatory Affairs Manager', 'Regulatory Affairs Executive',
  // Sales & BD
  'Sales Manager', 'Sales Executive', 'Business Development Manager', 'Business Development Executive',
  'Medical Representative', 'Territory Manager', 'Key Account Manager',
  // Finance & Accounts
  'Finance Manager', 'Senior Accountant', 'Accountant', 'Finance Executive',
  // Procurement & Logistics
  'Procurement Manager', 'Procurement Executive', 'Logistics Manager', 'Logistics Coordinator', 'Supply Chain Executive',
  // HR & Admin
  'HR Manager', 'HR Executive', 'Admin Manager', 'Admin Executive',
  // IT
  'Software Engineer', 'Senior Software Engineer', 'IT Manager', 'IT Executive',
  // Operations
  'Operations Manager', 'Operations Executive',
];

const EMPTY_EMP = {
  employee_code:'', first_name:'', last_name:'', email:'', phone:'', alternate_phone:'',
  dob:'', gender:'', blood_group:'', nationality:'Indian', marital_status:'',
  address:'', city:'', state:'', pincode:'', country:'India',
  emergency_name:'', emergency_relation:'', emergency_phone:'',
  department:'', designation:'', employment_type:'full_time', work_location:'', joining_date:'', confirmation_date:'',
  reporting_to:'', salary:'', salary_currency:'INR', pay_frequency:'monthly',
  pan_number:'', aadhaar_number:'', status:'active', exit_date:'', exit_reason:'', notes:'', user_id:'',
  bank_name:'', bank_account:'', bank_ifsc:'', bank_branch:'', upi_id:'', payment_notes:'',
};

/* ── Avatar ────────────────────────────────────────────────────── */
function Avatar({ name, size = 38 }) {
  const initials = (name || '?').split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase();
  const hue = (name?.charCodeAt(0) || 65) * 17 % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `hsl(${hue},50%,88%)`, color: `hsl(${hue},50%,30%)`,
      display:'flex', alignItems:'center', justifyContent:'center',
      fontWeight: 700, fontSize: size * 0.38,
    }}>{initials}</div>
  );
}

/* ── Badge ─────────────────────────────────────────────────────── */
function Badge({ text, bg, color }) {
  return <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'2px 10px', borderRadius:20, background:bg, color }}>{text}</span>;
}

/* ── Section heading (inside modal) ─────────────────────────────── */
function SectionHead({ title }) {
  return (
    <div style={{ fontSize:'0.72rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12, marginTop:4, borderBottom:'2px solid #eff6ff', paddingBottom:6 }}>
      {title}
    </div>
  );
}

/* ── Form field ─────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const inp = { width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#0f172a', background:'#fff', boxSizing:'border-box', outline:'none' };
const sel = { ...inp };

/* ── Designation combo-box ─────────────────────────────────────── */
function DesignationPicker({ value, onChange }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState(value || '');
  const ref = React.useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const filtered = DESIGNATIONS.filter(d =>
    !query || d.toLowerCase().includes(query.toLowerCase())
  );

  const handleInput = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  };

  const handleSelect = (d) => {
    setQuery(d);
    onChange(d);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...inp, paddingRight: 30 }}
          value={query}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
          placeholder="Select or type a designation…"
          autoComplete="off"
        />
        <svg width="14" height="14" fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24"
          style={{ position:'absolute', right:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && filtered.length > 0 && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:200,
          background:'#fff', border:'1px solid #e2e8f0', borderRadius:8,
          boxShadow:'0 4px 18px rgba(0,0,0,0.10)', marginTop:3,
          maxHeight:210, overflowY:'auto',
        }}>
          {filtered.map(d => {
            const active = d === value;
            return (
              <div
                key={d}
                onMouseDown={() => handleSelect(d)}
                style={{
                  padding:'8px 12px', cursor:'pointer', fontSize:'0.875rem',
                  color: active ? '#1d4ed8' : '#0f172a',
                  background: active ? '#eff6ff' : 'transparent',
                  fontWeight: active ? 600 : 400,
                  borderBottom:'1px solid #f1f5f9',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                {d}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EMPTY_SS = { basic:'', hra:'', da:'', ta:'', special_allowance:'', other_allowance:'',
  pf_employee_pct:'12', pf_employer_pct:'12', pt_monthly:'', tds_monthly:'',
  effective_from:'', notes:'' };

/* ══════════════════════════════════════════════════════════════ */
export default function HR() {
  const { isMobile } = useResponsive();
  const { isAdmin, isManager, canAccessModule } = useAuth();
  const canManageHR = canAccessModule('hr', 'can_list');
  const [tab, setTab]         = useState(() => canManageHR ? 'directory' : 'my_employment');
  const [employees, setEmps]  = useState([]);
  const [leaves, setLeaves]   = useState([]);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [deptFilter, setDept] = useState('');
  const [statusFilter, setSt] = useState('');
  const [sortBy, setSortBy]   = useState('employee_code_desc'); // Default: employee code descending (bottom to top)
  const [leaveFilter, setLF]  = useState('');
  const [lTypeFilter, setLT]  = useState('');
  const [page, setPage]       = useState(1);
  const [pagination, setPag]  = useState({});
  const [modal, setModal]     = useState(false);
  const [viewEmp, setViewEmp] = useState(null);
  const [leaveModal, setLM]   = useState(false);
  const [form, setForm]       = useState(EMPTY_EMP);
  const [leaveForm, setLF2]   = useState({ employee_id:'', leave_type:'sick', from_date:'', to_date:'', days:'', reason:'' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [deleteId, setDelId]  = useState(null);
  const [allEmps, setAllEmps] = useState([]);
  // HR config (holidays / working days / leaves allowed)
  const [hrConfig, setHrConfig]             = useState({ hr_holidays_per_year:'10', hr_working_days_per_month:'26', hr_leaves_per_year:'18' });
  const [positionLeaves, setPositionLeaves]       = useState([]); // [{ position, max_leaves }]
  const [monthlyWorkingDays, setMonthlyWorkingDays] = useState({}); // { "1": 24, "3": 20, ... }
  const [holidaysList, setHolidaysList]             = useState([]); // [{ date, name }]
  const [hrConfigSaving, setHrConfigSaving] = useState(false);
  const [hrConfigMsg, setHrConfigMsg]       = useState(null);
  const [leaveSummary, setLeaveSummary]     = useState([]);
  // Payroll tab
  const [payrollMonth, setPayrollMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [payrollData, setPayrollData]   = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollMsg, setPayrollMsg]     = useState(null);
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [editPayrollId, setEditPayrollId] = useState(null);
  const [editPayrollForm, setEditPayrollForm] = useState({});
  // Salary structure (per employee)
  const [salaryStruct, setSalaryStruct] = useState(null);
  const [salaryStructSaving, setSalaryStructSaving] = useState(false);
  const [salaryStructMsg, setSalaryStructMsg] = useState(null);
  const [showSalaryEdit, setShowSalaryEdit] = useState(false);
  const [salaryStructForm, setSalaryStructForm] = useState(EMPTY_SS);
  // Pay revisions
  const [payRevisions, setPayRevisions] = useState([]);
  const [payRevForm, setPayRevForm] = useState({ effective_date:'', revision_type:'increment', new_salary:'', currency:'INR', notes:'' });
  const [payRevSaving, setPayRevSaving] = useState(false);
  const [payRevError, setPayRevError] = useState('');
  // Employee documents
  const [empDocs, setEmpDocs] = useState([]);
  const [empDocForm, setEmpDocForm] = useState({ doc_type:'offer_letter', doc_description:'', file:null });
  const [empDocUploading, setEmpDocUploading] = useState(false);
  const [empDocError, setEmpDocError] = useState('');
  // Self-service: My Employment
  const [myProfile, setMyProfile] = useState(null);
  const [myPayslips, setMyPayslips] = useState([]);
  const [myProfileLoading, setMyProfileLoading] = useState(false);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  /* load HR config from company settings */
  const loadHrConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/company-settings');
      const d = data.data || {};
      setHrConfig({
        hr_holidays_per_year:      d.hr_holidays_per_year      || '10',
        hr_working_days_per_month: d.hr_working_days_per_month || '26',
        hr_leaves_per_year:        d.hr_leaves_per_year        || '18',
      });
      setPositionLeaves(Array.isArray(d.hr_position_leaves) ? d.hr_position_leaves : []);
      setMonthlyWorkingDays(d.hr_monthly_working_days && typeof d.hr_monthly_working_days === 'object' ? d.hr_monthly_working_days : {});
      setHolidaysList(Array.isArray(d.hr_holidays_list) ? d.hr_holidays_list : []);
    } catch {}
  }, []);

  /* load payroll */
  const loadPayroll = useCallback(async (month) => {
    setPayrollLoading(true); setPayrollMsg(null);
    try {
      // month param is always passed explicitly; fall back to state for tab-switch loads
      const m = month || new Date().toISOString().slice(0, 7);
      const { data } = await api.get(`/hr/payroll?month=${m}`);
      setPayrollData(data.data || []);
    } catch { setPayrollMsg({ type:'error', text:'Failed to load payroll.' }); }
    finally { setPayrollLoading(false); }
  }, []);

  /* load leave summary */
  const loadLeaveSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/hr/leaves/summary');
      setLeaveSummary(data.data || []);
    } catch {}
  }, []);

  /* load stats */
  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/hr/stats');
      setStats(data.data);
    } catch {}
  }, []);

  /* load employees */
  const loadEmps = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search)      params.set('search', search);
      if (deptFilter)  params.set('department', deptFilter);
      if (statusFilter)params.set('status', statusFilter);
      if (sortBy)      params.set('sort_by', sortBy);
      const { data } = await api.get(`/hr?${params}`);
      setEmps(data.data);
      setPag(data.pagination);
    } catch { setError('Failed to load employees.'); }
    finally  { setLoading(false); }
  }, [page, search, deptFilter, statusFilter, sortBy]);

  /* load leaves */
  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (leaveFilter) params.set('status', leaveFilter);
      if (lTypeFilter) params.set('leave_type', lTypeFilter);
      const { data } = await api.get(`/hr/leaves/all?${params}`);
      setLeaves(data.data);
    } catch {}
    finally { setLoading(false); }
  }, [leaveFilter, lTypeFilter]);

  useEffect(() => { loadStats(); loadHrConfig(); }, [loadStats, loadHrConfig]);

  useEffect(() => {
    if (tab === 'directory')    loadEmps();
    if (tab === 'orgchart')     fetchAllEmps();
    if (tab === 'leaves')       { loadLeaves(); loadLeaveSummary(); }
    if (tab === 'settings')     { loadHrConfig(); loadLeaveSummary(); }
    if (tab === 'payroll')      loadPayroll(payrollMonth);
    if (tab === 'my_employment') {
      setMyProfileLoading(true);
      Promise.all([
        api.get('/hr/me').then(r => setMyProfile(r.data.data)).catch(() => setMyProfile(null)),
        api.get('/hr/me/payslips').then(r => setMyPayslips(r.data.data || [])).catch(() => setMyPayslips([])),
      ]).finally(() => setMyProfileLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loadEmps, loadLeaves, loadHrConfig, loadLeaveSummary, loadPayroll]);

  /* load all employees for dropdowns */
  const fetchAllEmps = async () => {
    if (allEmps.length) return;
    try {
      const { data } = await api.get('/hr?limit=200');
      setAllEmps(data.data);
    } catch {}
  };

  const openAdd = async () => {
    await fetchAllEmps();
    let nextCode = '';
    try {
      const { data } = await api.get('/hr/next-code');
      nextCode = data.code || '';
    } catch {}
    setForm({ ...EMPTY_EMP, employee_code: nextCode });
    setModal(true);
  };

  const openEdit = async (emp) => {
    await fetchAllEmps();
    const { data } = await api.get(`/hr/${emp.id}`);
    const e = data.data;
    setForm({
      ...EMPTY_EMP, ...e,
      dob: e.dob ? e.dob.split('T')[0] : '',
      joining_date: e.joining_date ? e.joining_date.split('T')[0] : '',
      confirmation_date: e.confirmation_date ? e.confirmation_date.split('T')[0] : '',
      exit_date: e.exit_date ? e.exit_date.split('T')[0] : '',
      reporting_to: e.reporting_to || '',
      user_id: e.user_id || '',
    });
    setModal(true);
  };

  const loadSalaryStruct = async (empId) => {
    try {
      const { data } = await api.get(`/hr/${empId}/salary-structure`);
      setSalaryStruct(data.data);
      setSalaryStructForm(data.data ? {
        basic: data.data.basic || '', hra: data.data.hra || '', da: data.data.da || '',
        ta: data.data.ta || '', special_allowance: data.data.special_allowance || '',
        other_allowance: data.data.other_allowance || '',
        pf_employee_pct: data.data.pf_employee_pct ?? '12',
        pf_employer_pct: data.data.pf_employer_pct ?? '12',
        pt_monthly: data.data.pt_monthly || '', tds_monthly: data.data.tds_monthly || '',
        effective_from: data.data.effective_from ? data.data.effective_from.split('T')[0] : '',
        notes: data.data.notes || '',
      } : EMPTY_SS);
    } catch {}
  };

  const saveSalaryStruct = async (empId) => {
    setSalaryStructSaving(true); setSalaryStructMsg(null);
    try {
      await api.put(`/hr/${empId}/salary-structure`, salaryStructForm);
      setSalaryStructMsg({ type:'success', text:'Salary structure saved.' });
      await loadSalaryStruct(empId);
      setShowSalaryEdit(false);
    } catch (e) {
      setSalaryStructMsg({ type:'error', text: e.response?.data?.message || 'Save failed.' });
    } finally { setSalaryStructSaving(false); }
  };

  const openView = async (emp) => {
    const { data } = await api.get(`/hr/${emp.id}`);
    setViewEmp(data.data);
    setSalaryStruct(null); setSalaryStructMsg(null); setShowSalaryEdit(false);
    loadSalaryStruct(emp.id);
    setPayRevError(''); setEmpDocError('');
    setPayRevForm({ effective_date:'', revision_type:'increment', new_salary:'', currency:'INR', notes:'' });
    setEmpDocForm({ doc_type:'offer_letter', doc_description:'', file:null });
    const [revRes, docRes] = await Promise.all([
      api.get(`/hr/${emp.id}/pay-revisions`),
      api.get(`/hr/${emp.id}/documents`),
    ]);
    setPayRevisions(revRes.data.data || []);
    setEmpDocs(docRes.data.data || []);
  };

  const refreshPayRevisions = async (empId) => {
    const { data } = await api.get(`/hr/${empId}/pay-revisions`);
    setPayRevisions(data.data || []);
    // Refresh employee data so salary shows updated value
    const { data: empData } = await api.get(`/hr/${empId}`);
    setViewEmp(empData.data);
  };

  const refreshEmpDocs = async (empId) => {
    const { data } = await api.get(`/hr/${empId}/documents`);
    setEmpDocs(data.data || []);
  };

  const handleAddPayRevision = async () => {
    if (!payRevForm.effective_date || !payRevForm.new_salary) { setPayRevError('Effective date and new salary are required.'); return; }
    setPayRevSaving(true); setPayRevError('');
    try {
      await api.post(`/hr/${viewEmp.id}/pay-revisions`, payRevForm);
      setPayRevForm({ effective_date:'', revision_type:'increment', new_salary:'', currency:'INR', notes:'' });
      await refreshPayRevisions(viewEmp.id);
    } catch (e) { setPayRevError(e.response?.data?.message || 'Failed to save.'); }
    finally { setPayRevSaving(false); }
  };

  const handleDeletePayRevision = async (revId) => {
    if (!window.confirm('Delete this pay revision?')) return;
    await api.delete(`/hr/${viewEmp.id}/pay-revisions/${revId}`);
    await refreshPayRevisions(viewEmp.id);
  };

  const handleEmpDocUpload = async () => {
    if (!empDocForm.file) { setEmpDocError('Please select a file.'); return; }
    if (empDocForm.doc_type === 'others' && !empDocForm.doc_description.trim()) { setEmpDocError('Please describe the document type.'); return; }
    setEmpDocUploading(true); setEmpDocError('');
    try {
      const fd = new FormData();
      fd.append('file', empDocForm.file);
      fd.append('doc_type', empDocForm.doc_type);
      if (empDocForm.doc_description) fd.append('doc_description', empDocForm.doc_description);
      await api.post(`/hr/${viewEmp.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEmpDocForm({ doc_type:'offer_letter', doc_description:'', file:null });
      await refreshEmpDocs(viewEmp.id);
    } catch (e) { setEmpDocError(e.response?.data?.message || 'Upload failed.'); }
    finally { setEmpDocUploading(false); }
  };

  const handleEmpDocDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await api.delete(`/hr/${viewEmp.id}/documents/${docId}`);
    await refreshEmpDocs(viewEmp.id);
  };

  const handleEmpDocDownload = (docId, name) => {
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL || '/api'}/hr/${viewEmp.id}/documents/${docId}/download`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); });
  };

  const empDocIcon = (filename = '') => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['xls','xlsx'].includes(ext)) return '📊';
    if (['png','jpg','jpeg','webp'].includes(ext)) return '🖼️';
    return '📎';
  };

  const EMP_DOC_TYPES = {
    offer_letter: 'Offer Letter', appointment_letter: 'Appointment Letter',
    id_proof: 'ID Proof', contract: 'Contract',
    appraisal: 'Appraisal', certificate: 'Certificate', others: 'Others',
  };

  const PAY_REV_TYPES = {
    increment: 'Increment', promotion: 'Promotion',
    correction: 'Correction', joining: 'Joining', other: 'Other',
  };

  const handleHrConfigSave = async () => {
    setHrConfigSaving(true); setHrConfigMsg(null);
    try {
      await api.put('/company-settings', { ...hrConfig, hr_position_leaves: positionLeaves, hr_monthly_working_days: monthlyWorkingDays, hr_holidays_list: holidaysList });
      setHrConfigMsg({ type: 'success', text: 'HR settings saved.' });
    } catch {
      setHrConfigMsg({ type: 'error', text: 'Save failed.' });
    } finally { setHrConfigSaving(false); }
  };

  const handleSave = async () => {
    if (!form.first_name || !form.email) return setError('First name and email are required.');
    setSaving(true); setError('');
    try {
      if (form.id) await api.put(`/hr/${form.id}`, form);
      else         await api.post('/hr', form);
      setModal(false);
      loadEmps(); loadStats();
    } catch (err) { setError(err.response?.data?.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/hr/${deleteId}`);
      setDelId(null);
      if (viewEmp?.id === deleteId) setViewEmp(null);
      loadEmps(); loadStats();
    } catch { setError('Freeze failed.'); }
  };

  const handleUnfreeze = async (id) => {
    try {
      await api.patch(`/hr/${id}/unfreeze`);
      loadEmps(); loadStats();
      if (viewEmp?.id === id) { const { data } = await api.get(`/hr/${id}`); setViewEmp(data.data); }
    } catch { setError('Unfreeze failed.'); }
  };

  const handleLeaveSubmit = async () => {
    if (!leaveForm.employee_id || !leaveForm.from_date || !leaveForm.to_date || !leaveForm.days)
      return setError('Fill all required leave fields.');
    setSaving(true); setError('');
    try {
      await api.post('/hr/leaves', leaveForm);
      setLM(false); loadLeaves(); loadStats();
    } catch (err) { setError(err.response?.data?.message || 'Failed to submit leave.'); }
    finally { setSaving(false); }
  };

  const handleLeaveAction = async (leaveId, status, approverEmpId) => {
    try {
      await api.patch(`/hr/leaves/${leaveId}/status`, { status, approver_employee_id: approverEmpId });
      loadLeaves(); loadStats();
    } catch { setError('Action failed.'); }
  };

  /* ── Stats strip ──────────────────────────────────────────── */
  const StatCard = ({ label, value, accent }) => (
    <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'16px 20px', flex:1, minWidth:120 }}>
      <div style={{ fontSize:'1.6rem', fontWeight:900, color: accent || '#0f172a' }}>{value ?? '—'}</div>
      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginTop:3 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ background:'#f8fafc', minHeight:'100vh' }}>

      {/* ── Header ───────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding: isMobile ? '20px 16px 0' : '24px 32px 0' }}>
        <div style={{ maxWidth:1400, margin:'0 auto' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap:12, marginBottom:20 }}>
            <div>
              <h1 style={{ fontSize:'1.5rem', fontWeight:900, color:'#0f172a', letterSpacing:'-0.4px', marginBottom:4 }}>
                {canManageHR ? 'Human Resources' : 'My Employment'}
              </h1>
              <p style={{ fontSize:'0.85rem', color:'#64748b' }}>
                {canManageHR ? 'Employee directory, profiles and leave management' : 'Your employment profile and career timeline'}
              </p>
            </div>
            {canManageHR && (
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
                <ExportButton endpoint="/export/employees" params={{}} label="Export" filename="employees" />
                <button onClick={() => { setLM(true); fetchAllEmps(); }} style={{ padding:'9px 18px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', color:'#374151', fontSize:'0.83rem', fontWeight:600, cursor:'pointer' }}>
                  + Leave Request
                </button>
                <button onClick={openAdd} style={{ padding:'9px 18px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor:'pointer' }}>
                  + Add Employee
                </button>
              </div>
            )}
          </div>

          {/* Stats — admin/manager only */}
          {canManageHR && stats && (
            <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
              <StatCard label="Total Employees" value={stats.total} />
              <StatCard label="Active"           value={stats.active}  accent="#059669" />
              <StatCard label="On Leave"         value={stats.on_leave} accent="#d97706" />
              <StatCard label="On Notice"        value={stats.on_notice} accent="#ea580c" />
              <StatCard label="Departments"      value={stats.departments} accent="#7c3aed" />
              <StatCard label="Pending Leaves"   value={stats.pending_leaves} accent="#dc2626" />
            </div>
          )}

          {/* Tabs */}
          <div style={{ display:'flex', gap:2, flexWrap:'wrap' }}>
            {[
              ['my_employment', 'My Employment'],
              ...(canManageHR ? [
                ['directory','Directory'],
                ['orgchart','Org Chart'],
                ['leaves','Leave Management'],
                ['payroll','Payroll'],
                ['settings','HR Settings'],
              ] : []),
            ].map(([k,l]) => (
              <button key={k} onClick={() => setTab(k)} style={{
                padding:'10px 20px', border:'none', cursor:'pointer', fontSize:'0.83rem',
                fontWeight: tab===k ? 700 : 500,
                color: tab===k ? '#1d4ed8' : '#64748b',
                background:'transparent',
                borderBottom: tab===k ? '2.5px solid #1d4ed8' : '2.5px solid transparent',
              }}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1400, margin:'0 auto', padding: isMobile ? '16px 16px 48px' : '28px 32px 64px' }}>

        {error && <div style={{ background:'#fee2e2', color:'#dc2626', borderRadius:8, padding:'10px 16px', marginBottom:16, fontSize:'0.875rem' }}>{error}</div>}

        {/* ══ DIRECTORY TAB ══════════════════════════════════ */}
        {tab === 'directory' && (
          <>
            {/* Filters */}
            <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, email, code, designation…"
                style={{ flex:1, minWidth:220, padding:'9px 14px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', outline:'none' }} />
              <select value={deptFilter} onChange={e => { setDept(e.target.value); setPage(1); }}
                style={{ padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#374151', background:'#fff' }}>
                <option value="">All Departments</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={statusFilter} onChange={e => { setSt(e.target.value); setPage(1); }}
                style={{ padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#374151', background:'#fff' }}>
                <option value="">All Status</option>
                {Object.entries(STATUS_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
                style={{ padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#374151', background:'#fff' }}>
                <option value="employee_code_desc">Employee Code (Z-A)</option>
                <option value="employee_code_asc">Employee Code (A-Z)</option>
                <option value="joining_date_desc">Joining Date (Newest First)</option>
                <option value="joining_date_asc">Joining Date (Oldest First)</option>
              </select>
            </div>

            {/* Employee grid */}
            {loading ? (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:'#94a3b8' }}>Loading…</div>
            ) : employees.length === 0 ? (
              <div style={{ textAlign:'center', color:'#94a3b8', padding:'60px 0', fontSize:'0.9rem' }}>No employees found. Add your first employee.</div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
                {employees.map(e => {
                  const sm = STATUS_META[e.status] || STATUS_META.active;
                  const em = EMP_TYPE_META[e.employment_type] || EMP_TYPE_META.full_time;
                  return (
                    <div key={e.id} style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, padding:'20px 22px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', cursor:'pointer', transition:'box-shadow 0.15s' }}
                      onClick={() => openView(e)}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:14, marginBottom:14 }}>
                        <Avatar name={`${e.first_name} ${e.last_name || ''}`} size={44} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                            <div style={{ fontWeight:800, fontSize:'0.95rem', color: !e.is_active ? '#94a3b8' : '#0f172a', lineHeight:1.2 }}>
                              {e.first_name} {e.last_name}
                              {!e.is_active && <span style={{ marginLeft:8, fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#f1f5f9', color:'#64748b' }}>Frozen</span>}
                            </div>
                            <Badge text={sm.label} bg={sm.bg} color={sm.color} />
                          </div>
                          <div style={{ fontSize:'0.78rem', color:'#64748b', marginTop:3 }}>{e.designation || '—'}</div>
                          <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:2 }}>{e.employee_code}</div>
                        </div>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
                        <div>
                          <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase' }}>Department</div>
                          <div style={{ fontSize:'0.8rem', color:'#374151', fontWeight:500, marginTop:2 }}>{e.department || '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase' }}>Joined</div>
                          <div style={{ fontSize:'0.8rem', color:'#374151', marginTop:2 }}>{fmtDate(e.joining_date)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase' }}>Reports To</div>
                          <div style={{ fontSize:'0.8rem', color:'#374151', marginTop:2 }}>{e.reporting_to_name?.trim() || '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase' }}>Location</div>
                          <div style={{ fontSize:'0.8rem', color:'#374151', marginTop:2 }}>{e.work_location || '—'}</div>
                        </div>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <Badge text={em.label} bg={em.bg} color={em.color} />
                        <div style={{ display:'flex', gap:8 }}>
                          {!e.is_active
                            ? isAdmin && <button onClick={ev => { ev.stopPropagation(); handleUnfreeze(e.id); }} style={{ padding:'4px 12px', border:'1px solid #bbf7d0', borderRadius:6, background:'#fff', color:'#16a34a', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>Unfreeze</button>
                            : <>
                                <button onClick={ev => { ev.stopPropagation(); openEdit(e); }} style={{ padding:'4px 12px', border:'1px solid #e2e8f0', borderRadius:6, background:'#fff', color:'#374151', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>Edit</button>
                                {isAdmin && <button onClick={ev => { ev.stopPropagation(); setDelId(e.id); }} style={{ padding:'4px 12px', border:'1px solid #fee2e2', borderRadius:6, background:'#fff', color:'#dc2626', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>Freeze</button>}
                              </>
                          }
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:28 }}>
                {Array.from({ length: pagination.pages }, (_, i) => i+1).map(p => (
                  <button key={p} onClick={() => setPage(p)} style={{
                    width:34, height:34, borderRadius:8, border:'1px solid #e2e8f0', cursor:'pointer', fontSize:'0.82rem', fontWeight: p===page ? 700 : 400,
                    background: p===page ? '#1d4ed8' : '#fff', color: p===page ? '#fff' : '#374151',
                  }}>{p}</button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ══ LEAVES TAB ════════════════════════════════════ */}
        {tab === 'leaves' && (
          <>
            {/* ── Leave Balance Tracker ──────────────────── */}
            {leaveSummary.length > 0 && (() => {
              const defaultAllowed = parseInt(hrConfig.hr_leaves_per_year, 10) || 18;
              const posMap = {};
              positionLeaves.forEach(r => { if (r.position) posMap[r.position.toLowerCase()] = parseInt(r.max_leaves, 10) || defaultAllowed; });
              const getAllowed = (emp) => {
                if (emp.designation) {
                  const key = (emp.designation || '').toLowerCase();
                  for (const [pos, val] of Object.entries(posMap)) {
                    if (key.includes(pos) || pos.includes(key)) return val;
                  }
                }
                return defaultAllowed;
              };
              return (
                <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, marginBottom:20, overflow:'hidden' }}>
                  <div style={{ padding:'12px 18px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <span style={{ fontWeight:700, fontSize:'0.875rem', color:'#0f172a' }}>Leave Balance Tracker</span>
                      <span style={{ fontSize:'0.72rem', color:'#64748b', marginLeft:10 }}>{new Date().getFullYear()} · Default: <b>{defaultAllowed} days/year</b></span>
                    </div>
                  </div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem', minWidth:540 }}>
                      <thead>
                        <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                          {['Employee','Dept','Approved Days','Pending','Allowed','Balance','Status'].map(h => (
                            <th key={h} style={{ padding:'8px 14px', textAlign:'left', fontSize:'0.7rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {leaveSummary.map((emp, i) => {
                          const allowed = getAllowed(emp);
                          const taken   = Number(emp.approved_days) || 0;
                          const pending = Number(emp.pending_days)  || 0;
                          const balance = allowed - taken;
                          const over    = taken > allowed;
                          const pct     = Math.min(100, Math.round((taken / allowed) * 100));
                          return (
                            <tr key={emp.id} style={{ borderBottom:'1px solid #f1f5f9', background: over ? '#fff8f8' : i%2===0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding:'10px 14px' }}>
                                <div style={{ fontWeight:600, color: over ? '#dc2626' : '#0f172a' }}>{emp.name?.trim()}</div>
                                <div style={{ fontSize:'0.7rem', color:'#94a3b8' }}>{emp.employee_code}</div>
                              </td>
                              <td style={{ padding:'10px 14px', color:'#64748b', fontSize:'0.78rem' }}>{emp.department || '—'}</td>
                              <td style={{ padding:'10px 14px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <span style={{ fontWeight:700, color: over ? '#dc2626' : '#0f172a' }}>{taken}</span>
                                  <div style={{ flex:1, minWidth:60, height:6, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                                    <div style={{ width:`${pct}%`, height:'100%', background: over ? '#ef4444' : pct>=80 ? '#f97316' : '#10b981', borderRadius:4, transition:'width 0.3s' }} />
                                  </div>
                                  <span style={{ fontSize:'0.68rem', color:'#94a3b8' }}>{pct}%</span>
                                </div>
                              </td>
                              <td style={{ padding:'10px 14px', color:'#f59e0b', fontWeight:600 }}>
                                {pending > 0 ? `+${pending}` : '—'}
                              </td>
                              <td style={{ padding:'10px 14px', color:'#64748b', fontWeight:600 }}>
                                {allowed}
                                {allowed !== defaultAllowed && (
                                  <span style={{ fontSize:'0.65rem', marginLeft:4, color:'#6366f1' }}>★</span>
                                )}
                              </td>
                              <td style={{ padding:'10px 14px' }}>
                                {over
                                  ? <span style={{ fontWeight:800, color:'#dc2626' }}>−{taken - allowed} over</span>
                                  : <span style={{ fontWeight:700, color: balance <= 3 ? '#f97316' : '#16a34a' }}>{balance} left</span>
                                }
                              </td>
                              <td style={{ padding:'10px 14px' }}>
                                {over
                                  ? <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 9px', borderRadius:20, background:'#fee2e2', color:'#dc2626' }}>Exceeded</span>
                                  : balance <= 3 && balance >= 0
                                    ? <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 9px', borderRadius:20, background:'#ffedd5', color:'#c2410c' }}>Low</span>
                                    : <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'3px 9px', borderRadius:20, background:'#dcfce7', color:'#15803d' }}>OK</span>
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
              <select value={leaveFilter} onChange={e => setLF(e.target.value)}
                style={{ padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#374151', background:'#fff' }}>
                <option value="">All Status</option>
                {Object.entries(LEAVE_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={lTypeFilter} onChange={e => setLT(e.target.value)}
                style={{ padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#374151', background:'#fff' }}>
                <option value="">All Types</option>
                {LEAVE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1).replace('_',' ')}</option>)}
              </select>
            </div>

            <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, overflow:'hidden' }}>
              <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
                <thead>
                  <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                    {['Employee','Type','From','To','Days','Reason','Status','Action'].map(h => (
                      <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>Loading…</td></tr>
                  ) : leaves.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>No leave records found.</td></tr>
                  ) : leaves.map((l, idx) => {
                    const lm = LEAVE_STATUS[l.status] || LEAVE_STATUS.pending;
                    return (
                      <tr key={l.id} style={{ borderBottom:'1px solid #f1f5f9', background: idx%2===0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding:'12px 16px' }}>
                          <div style={{ fontWeight:700, fontSize:'0.85rem', color:'#0f172a' }}>{l.employee_name}</div>
                          <div style={{ fontSize:'0.72rem', color:'#94a3b8' }}>{l.employee_code} · {l.department}</div>
                        </td>
                        <td style={{ padding:'12px 16px', fontSize:'0.82rem', color:'#374151', textTransform:'capitalize' }}>
                          {l.leave_type.replace('_',' ')}
                        </td>
                        <td style={{ padding:'12px 16px', fontSize:'0.82rem', color:'#374151', whiteSpace:'nowrap' }}>{fmtDate(l.from_date)}</td>
                        <td style={{ padding:'12px 16px', fontSize:'0.82rem', color:'#374151', whiteSpace:'nowrap' }}>{fmtDate(l.to_date)}</td>
                        <td style={{ padding:'12px 16px', fontSize:'0.85rem', fontWeight:700, color:'#0f172a' }}>{l.days}</td>
                        <td style={{ padding:'12px 16px', fontSize:'0.8rem', color:'#64748b', maxWidth:200 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.reason || '—'}</div>
                        </td>
                        <td style={{ padding:'12px 16px' }}>
                          <Badge text={lm.label} bg={lm.bg} color={lm.color} />
                        </td>
                        <td style={{ padding:'12px 16px' }}>
                          {l.status === 'pending' && (
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={() => handleLeaveAction(l.id,'approved', null)}
                                style={{ padding:'3px 10px', border:'none', borderRadius:6, background:'#d1fae5', color:'#065f46', fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>
                                Approve
                              </button>
                              <button onClick={() => handleLeaveAction(l.id,'rejected', null)}
                                style={{ padding:'3px 10px', border:'none', borderRadius:6, background:'#fee2e2', color:'#991b1b', fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>
                                Reject
                              </button>
                            </div>
                          )}
                          {l.status !== 'pending' && (
                            <div style={{ fontSize:'0.72rem', color:'#94a3b8' }}>
                              {l.approved_by_name?.trim() ? `by ${l.approved_by_name}` : 'Processed'}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}

        {/* ══ PAYROLL TAB ════════════════════════════════════ */}
        {tab === 'payroll' && (
          <>
            {/* Controls */}
            <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:20, flexWrap:'wrap' }}>
              <div>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Payroll Month</label>
                <input type="month" value={payrollMonth}
                  onChange={e => { setPayrollMonth(e.target.value); loadPayroll(e.target.value); }}
                  style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem' }} />
              </div>
              {isAdmin && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Generate payroll for ${payrollMonth}? Existing entries will be skipped.`)) return;
                  setProcessingPayroll(true); setPayrollMsg(null);
                  try {
                    const { data } = await api.post('/hr/payroll/process', { month: payrollMonth, working_days: 26 });
                    setPayrollMsg({ type:'success', text: data.message });
                    await loadPayroll(payrollMonth);
                  } catch (e) { setPayrollMsg({ type:'error', text: e.response?.data?.message || 'Failed.' }); }
                  finally { setProcessingPayroll(false); }
                }}
                disabled={processingPayroll}
                style={{ marginTop:20, padding:'9px 18px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor:'pointer', opacity: processingPayroll ? 0.7 : 1 }}>
                {processingPayroll ? 'Processing...' : 'Generate Payroll'}
              </button>
              )}
              <button onClick={async () => {
                  if (!window.confirm(`Sync LOP from attendance records for ${payrollMonth}? This will update LOP days and recalculate net pay for all processed (unpaid) entries.`)) return;
                  setProcessingPayroll(true); setPayrollMsg(null);
                  try {
                    const { data } = await api.post('/hr/payroll/sync-lop', { month: payrollMonth });
                    setPayrollMsg({ type:'success', text: data.message });
                    await loadPayroll(payrollMonth);
                  } catch (e) { setPayrollMsg({ type:'error', text: e.response?.data?.message || 'Sync failed.' }); }
                  finally { setProcessingPayroll(false); }
                }}
                disabled={processingPayroll}
                title="Compute LOP from attendance records (absent=1 day, half_day=0.5 days)"
                style={{ marginTop:20, padding:'9px 14px', border:'1px solid #f59e0b', borderRadius:8, background:'#fffbeb', color:'#92400e', fontSize:'0.83rem', fontWeight:700, cursor:'pointer', opacity: processingPayroll ? 0.7 : 1 }}>
                Sync LOP from Attendance
              </button>
              <button onClick={() => loadPayroll(payrollMonth)}
                style={{ marginTop:20, padding:'9px 14px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', color:'#374151', fontSize:'0.83rem', cursor:'pointer' }}>
                Refresh
              </button>
            </div>

            {payrollMsg && (
              <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:'0.875rem',
                background: payrollMsg.type === 'success' ? '#d1fae5' : '#fee2e2',
                color: payrollMsg.type === 'success' ? '#065f46' : '#dc2626',
                border: `1px solid ${payrollMsg.type === 'success' ? '#6ee7b7' : '#fca5a5'}` }}>
                {payrollMsg.text}
              </div>
            )}

            {payrollLoading ? (
              <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>Loading payroll...</div>
            ) : payrollData.length === 0 ? (
              <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>No active employees found.</div>
            ) : (
              <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', overflow:'hidden' }}>
                {/* Payroll summary */}
                {(() => {
                  const processed = payrollData.filter(r => r.payroll_id);
                  const paid = payrollData.filter(r => r.status === 'paid');
                  const totalNet = processed.reduce((s,r) => s + parseFloat(r.net_pay||0), 0);
                  if (!processed.length) return null;
                  return (
                    <div style={{ display:'flex', gap:20, padding:'12px 20px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'0.8rem', color:'#64748b' }}><strong style={{ color:'#0f172a' }}>{processed.length}</strong> / {payrollData.length} processed</span>
                      <span style={{ fontSize:'0.8rem', color:'#064748' }}><strong style={{ color:'#059669' }}>{paid.length}</strong> paid</span>
                      <span style={{ fontSize:'0.8rem', color:'#64748b' }}>Total Net: <strong style={{ color:'#1d4ed8' }}>{'₹'}{totalNet.toLocaleString('en-IN', { maximumFractionDigits:0 })}</strong></span>
                    </div>
                  );
                })()}
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.83rem' }}>
                  <thead>
                    <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
                      {['Employee','Department','Basic','Gross','LOP Days','Deductions','Net Pay','Status','Actions'].map(h => (
                        <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payrollData.map((row, i) => {
                      const isEditing = editPayrollId !== null && editPayrollId === row.payroll_id;
                      const hasSalary = row.basic !== null && row.basic !== undefined;
                      const isProcessed = !!row.payroll_id;
                      return (
                        <tr key={row.employee_id} style={{ borderBottom:'1px solid #f1f5f9', background: i%2===0?'#fff':'#fafbfc' }}>
                          <td style={{ padding:'10px 12px' }}>
                            <div style={{ fontWeight:700, color:'#0f172a' }}>{row.name}</div>
                            <div style={{ fontSize:'0.72rem', color:'#64748b' }}>{row.employee_code}</div>
                          </td>
                          <td style={{ padding:'10px 12px', color:'#374151', fontSize:'0.8rem' }}>{row.department || '—'}</td>
                          <td style={{ padding:'10px 12px', color:'#374151' }}>
                            {hasSalary ? `₹${parseFloat(row.basic||0).toLocaleString('en-IN')}` : <span style={{ color:'#f59e0b', fontSize:'0.75rem' }}>No structure</span>}
                          </td>
                          <td style={{ padding:'10px 12px', color:'#374151' }}>
                            {isProcessed ? `₹${parseFloat(row.gross_earned||0).toLocaleString('en-IN', {maximumFractionDigits:0})}` : '—'}
                          </td>
                          <td style={{ padding:'10px 12px' }}>
                            {isEditing ? (
                              <input type="number" min="0" step="0.5"
                                value={editPayrollForm.lop_days ?? row.lop_days ?? 0}
                                onChange={e => setEditPayrollForm(p => ({ ...p, lop_days: e.target.value }))}
                                style={{ width:60, padding:'4px 8px', border:'1px solid #6366f1', borderRadius:6, fontSize:'0.83rem' }} />
                            ) : (
                              <span style={{ color: parseFloat(row.lop_days) > 0 ? '#dc2626' : '#374151' }}>
                                {isProcessed ? (parseFloat(row.lop_days)||0) : '—'}
                              </span>
                            )}
                          </td>
                          <td style={{ padding:'10px 12px', color:'#dc2626' }}>
                            {isProcessed ? `₹${(parseFloat(row.pf_employee||0)+parseFloat(row.pt||0)+parseFloat(row.tds||0)+parseFloat(row.other_deductions||0)).toLocaleString('en-IN', {maximumFractionDigits:0})}` : '—'}
                          </td>
                          <td style={{ padding:'10px 12px', fontWeight:700, color:'#059669' }}>
                            {isProcessed ? `₹${parseFloat(row.net_pay||0).toLocaleString('en-IN', {maximumFractionDigits:0})}` : '—'}
                          </td>
                          <td style={{ padding:'10px 12px' }}>
                            {isProcessed ? (
                              <span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:700,
                                background: row.status==='paid'?'#d1fae5': row.status==='processed'?'#dbeafe':'#fef3c7',
                                color: row.status==='paid'?'#065f46': row.status==='processed'?'#1e40af':'#92400e' }}>
                                {row.status === 'paid' ? 'Paid' : row.status === 'processed' ? 'Processed' : 'Draft'}
                              </span>
                            ) : <span style={{ fontSize:'0.75rem', color:'#94a3b8' }}>Not generated</span>}
                          </td>
                          <td style={{ padding:'10px 12px' }}>
                            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                              {/* No salary structure — nothing can be done */}
                              {!hasSalary && !isProcessed && (
                                <span style={{ fontSize:'0.75rem', color:'#cbd5e1' }}>—</span>
                              )}
                              {/* Has salary structure but no payroll entry yet — offer per-row generate (admin only) */}
                              {hasSalary && !isProcessed && !isEditing && isAdmin && (
                                <button onClick={async () => {
                                  try {
                                    await api.post('/hr/payroll/process', { month: payrollMonth, working_days: 26 });
                                    await loadPayroll(payrollMonth);
                                  } catch (err) { alert(err?.response?.data?.message || 'Generate failed. Please try again.'); }
                                }} style={{ padding:'4px 10px', border:'1px solid #d97706', borderRadius:6, background:'#fffbeb', color:'#d97706', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                                  Generate
                                </button>
                              )}
                              {/* Processed, not editing, not paid — Edit + Mark Paid */}
                              {isProcessed && !isEditing && row.status !== 'paid' && (
                                <button onClick={() => { setEditPayrollId(row.payroll_id); setEditPayrollForm({ lop_days: row.lop_days || 0, other_deductions: row.other_deductions || 0 }); }}
                                  style={{ padding:'4px 10px', border:'1px solid #6366f1', borderRadius:6, background:'#eef2ff', color:'#4f46e5', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                                  Edit
                                </button>
                              )}
                              {/* Editing — Save + Cancel */}
                              {isEditing && (
                                <>
                                  <button onClick={async () => {
                                    try {
                                      await api.put(`/hr/payroll/${row.payroll_id}`, editPayrollForm);
                                      setEditPayrollId(null);
                                      await loadPayroll(payrollMonth);
                                    } catch { alert('Update failed'); }
                                  }} style={{ padding:'4px 10px', border:'none', borderRadius:6, background:'#059669', color:'#fff', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>Save</button>
                                  <button title="Cancel" onClick={() => setEditPayrollId(null)}
                                    style={{ padding:'4px 10px', border:'1px solid #e2e8f0', borderRadius:6, background:'#fff', color:'#374151', fontSize:'0.75rem', cursor:'pointer' }}>✕</button>
                                </>
                              )}
                              {/* Mark Paid — admin only */}
                              {isProcessed && !isEditing && row.status !== 'paid' && isAdmin && (
                                <button onClick={async () => {
                                  if (!window.confirm(`Mark ${row.name}'s salary as paid?`)) return;
                                  try {
                                    await api.post(`/hr/payroll/${row.payroll_id}/paid`, {});
                                    await loadPayroll(payrollMonth);
                                  } catch { alert('Failed to mark as paid'); }
                                }} style={{ padding:'4px 10px', border:'1px solid #059669', borderRadius:6, background:'#f0fdf4', color:'#059669', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                                  Mark Paid
                                </button>
                              )}
                              {/* Download Payslip */}
                              {isProcessed && !isEditing && (
                                <button onClick={async () => {
                                  try {
                                    const resp = await api.get(`/hr/payroll/${row.payroll_id}/payslip`, { responseType: 'blob' });
                                    const url = URL.createObjectURL(resp.data);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `payslip-${row.employee_code}-${payrollMonth}.pdf`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  } catch { alert('Failed to download payslip.'); }
                                }} title="Download Payslip PDF"
                                  style={{ padding:'4px 10px', border:'1px solid #6366f1', borderRadius:6, background:'#eef2ff', color:'#4f46e5', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' }}>
                                  Payslip
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ══ ORG CHART TAB ═════════════════════════════════ */}
        {tab === 'orgchart' && (
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'16px 24px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#0f172a' }}>Organisation Chart</div>
                <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:2 }}>Reporting hierarchy — click a card to highlight</div>
              </div>
              <div style={{ fontSize:'0.78rem', color:'#64748b' }}>{allEmps.length} employees</div>
            </div>
            <OrgChart employees={allEmps} />
          </div>
        )}

        {/* ══ HR SETTINGS TAB ═══════════════════════════════ */}
        {tab === 'settings' && (
          <div style={{ maxWidth: 680 }}>
            {/* Leave Policy */}
            <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, marginBottom:20, overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc', fontWeight:700, fontSize:'0.9rem', color:'#0f172a' }}>
                Leave &amp; Working Day Policy
              </div>
              <div style={{ padding:'20px 24px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:20 }}>
                  <div>
                    <label style={{ fontSize:'0.75rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:6 }}>
                      Holidays per Year
                    </label>
                    <input type="number" min="0" max="30"
                      value={hrConfig.hr_holidays_per_year}
                      onChange={e => setHrConfig(p => ({ ...p, hr_holidays_per_year: e.target.value }))}
                      style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.9rem', fontWeight:700, color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                    />
                    <p style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:4 }}>National + company holidays per calendar year</p>
                  </div>
                  <div>
                    <label style={{ fontSize:'0.75rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:6 }}>
                      Working Days / Month
                    </label>
                    <input type="number" min="1" max="31"
                      value={hrConfig.hr_working_days_per_month}
                      onChange={e => setHrConfig(p => ({ ...p, hr_working_days_per_month: e.target.value }))}
                      style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.9rem', fontWeight:700, color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                    />
                    <p style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:4 }}>Standard working days per month (e.g. 26 for 6-day week)</p>
                  </div>
                  <div>
                    <label style={{ fontSize:'0.75rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:6 }}>
                      Leaves Allowed / Year
                    </label>
                    <input type="number" min="0" max="60"
                      value={hrConfig.hr_leaves_per_year}
                      onChange={e => setHrConfig(p => ({ ...p, hr_leaves_per_year: e.target.value }))}
                      style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.9rem', fontWeight:700, color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                    />
                    <p style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:4 }}>Total paid leave days allowed per employee per year</p>
                  </div>
                </div>

                {/* Summary card */}
                <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:10, padding:'12px 16px', marginBottom:20, display:'flex', gap:24, flexWrap:'wrap' }}>
                  {[
                    { label: 'Holidays / year',      value: hrConfig.hr_holidays_per_year,      color:'#0369a1' },
                    { label: 'Working days / month',  value: hrConfig.hr_working_days_per_month, color:'#0369a1' },
                    { label: 'Leaves allowed / year', value: hrConfig.hr_leaves_per_year,        color:'#0369a1' },
                    { label: 'Total working days / year', value: String(Math.round((parseInt(hrConfig.hr_working_days_per_month,10)||26) * 12 - (parseInt(hrConfig.hr_holidays_per_year,10)||10))), color:'#0369a1' },
                  ].map(({ label, value, color }) => (
                    <div key={label}>
                      <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
                      <div style={{ fontSize:'1.25rem', fontWeight:900, color }}>{value}</div>
                    </div>
                  ))}
                </div>

                {hrConfigMsg && (
                  <div style={{ padding:'8px 14px', borderRadius:8, marginBottom:14, fontSize:'0.84rem',
                    background: hrConfigMsg.type==='success' ? '#d1fae5' : '#fee2e2',
                    color:      hrConfigMsg.type==='success' ? '#065f46' : '#dc2626',
                    border:     `1px solid ${hrConfigMsg.type==='success' ? '#6ee7b7' : '#fca5a5'}`,
                  }}>
                    {hrConfigMsg.text}
                  </div>
                )}
                <button onClick={handleHrConfigSave} disabled={hrConfigSaving}
                  style={{ padding:'9px 24px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.85rem', fontWeight:700, cursor:'pointer' }}>
                  {hrConfigSaving ? 'Saving…' : 'Save Settings'}
                </button>
              </div>
            </div>

            {/* Working days per month overrides */}
            {(() => {
              const defaultDays = parseInt(hrConfig.hr_working_days_per_month, 10) || 26;
              const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              return (
                <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, marginBottom:20, overflow:'hidden' }}>
                  <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc' }}>
                    <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#0f172a' }}>Working Days per Month</div>
                    <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:2 }}>
                      Override the default <strong>{defaultDays} days</strong> for specific months. Leave blank to use the default.
                    </div>
                  </div>
                  <div style={{ padding:'16px 20px' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10 }}>
                      {MONTHS.map((name, idx) => {
                        const key = String(idx + 1);
                        const val = monthlyWorkingDays[key] !== undefined ? String(monthlyWorkingDays[key]) : '';
                        const isOverride = val !== '' && parseInt(val, 10) !== defaultDays;
                        return (
                          <div key={key}>
                            <label style={{ fontSize:'0.7rem', fontWeight:700, color: isOverride ? '#1d4ed8' : '#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:4 }}>
                              {name}
                              {isOverride && <span style={{ marginLeft:4, color:'#6366f1' }}>★</span>}
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="31"
                              placeholder={String(defaultDays)}
                              value={val}
                              onChange={e => {
                                const v = e.target.value;
                                setMonthlyWorkingDays(prev => {
                                  const next = { ...prev };
                                  if (v === '') delete next[key];
                                  else next[key] = v;
                                  return next;
                                });
                              }}
                              style={{
                                width:'100%', padding:'8px 10px', borderRadius:8, fontSize:'0.9rem',
                                fontWeight: isOverride ? 700 : 400,
                                color: isOverride ? '#1d4ed8' : '#374151',
                                border: isOverride ? '1.5px solid #6366f1' : '1px solid #e2e8f0',
                                background: isOverride ? '#eef2ff' : '#fff',
                                boxSizing:'border-box', outline:'none',
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'0.72rem', color:'#94a3b8' }}>
                        ★ = override active &nbsp;·&nbsp; blank = use default ({defaultDays} days)
                      </span>
                      <button type="button" onClick={() => setMonthlyWorkingDays({})}
                        style={{ fontSize:'0.75rem', color:'#94a3b8', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
                        Clear all overrides
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Max leave per position */}
            <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#0f172a' }}>Max Leave Allowed per Position</div>
                  <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:2 }}>Override the default leave limit for specific designations</div>
                </div>
                <button type="button" onClick={() => setPositionLeaves(p => [...p, { position:'', max_leaves:'' }])}
                  style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.78rem', fontWeight:600,
                    padding:'5px 13px', borderRadius:7, border:'1px solid #1d4ed8',
                    background:'#eff6ff', color:'#1d4ed8', cursor:'pointer' }}>
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Position
                </button>
              </div>
              <div style={{ padding:'16px 20px' }}>
                {positionLeaves.length === 0 ? (
                  <p style={{ fontSize:'0.84rem', color:'#94a3b8', margin:0 }}>
                    No position-specific limits set. All employees use the default <strong>{hrConfig.hr_leaves_per_year} days/year</strong>.
                  </p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 160px 36px', gap:'8px 12px' }}>
                      <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em' }}>Position / Designation</span>
                      <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em' }}>Max Leaves / Year</span>
                      <span />
                    </div>
                    {positionLeaves.map((row, idx) => (
                      <div key={idx} style={{ display:'grid', gridTemplateColumns:'1fr 160px 36px', gap:'8px 12px', alignItems:'center' }}>
                        <input
                          type="text"
                          placeholder="e.g. Senior Scientist"
                          value={row.position}
                          onChange={e => setPositionLeaves(p => p.map((r, i) => i===idx ? { ...r, position: e.target.value } : r))}
                          style={{ width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                        />
                        <input
                          type="number"
                          min="0"
                          max="365"
                          placeholder="e.g. 21"
                          value={row.max_leaves}
                          onChange={e => setPositionLeaves(p => p.map((r, i) => i===idx ? { ...r, max_leaves: e.target.value } : r))}
                          style={{ width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', fontWeight:700, color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                        />
                        <button type="button" onClick={() => setPositionLeaves(p => p.filter((_,i) => i!==idx))}
                          title="Remove"
                          style={{ width:32, height:32, borderRadius:7, border:'1px solid #fca5a5',
                            background:'#fff1f2', color:'#dc2626', cursor:'pointer',
                            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Holiday list */}
            <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, marginTop:20, overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0', background:'#f8fafc', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#0f172a' }}>
                    Holiday List
                    {holidaysList.length > 0 && (
                      <span style={{ marginLeft:8, fontSize:'0.72rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#fef3c7', color:'#92400e' }}>
                        {holidaysList.length} holiday{holidaysList.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#94a3b8', marginTop:2 }}>Company holidays for the year — shown in leave reports</div>
                </div>
                <button type="button"
                  onClick={() => setHolidaysList(p => [...p, { date: '', name: '' }])}
                  style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.78rem', fontWeight:600,
                    padding:'5px 13px', borderRadius:7, border:'1px solid #1d4ed8',
                    background:'#eff6ff', color:'#1d4ed8', cursor:'pointer' }}>
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Holiday
                </button>
              </div>
              <div style={{ padding:'16px 20px' }}>
                {holidaysList.length === 0 ? (
                  <p style={{ fontSize:'0.84rem', color:'#94a3b8', margin:0 }}>No holidays added yet. Click "Add Holiday" to add one.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 36px', gap:'8px 12px' }}>
                      <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em' }}>Date</span>
                      <span style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em' }}>Holiday Name</span>
                      <span />
                    </div>
                    {holidaysList.map((h, idx) => {
                      const dayLabel = h.date ? new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short' }) : '';
                      return (
                        <div key={idx} style={{ display:'grid', gridTemplateColumns:'160px 1fr 36px', gap:'8px 12px', alignItems:'center' }}>
                          <div style={{ position:'relative' }}>
                            <input
                              type="date"
                              value={h.date}
                              onChange={e => setHolidaysList(p => p.map((r, i) => i===idx ? { ...r, date: e.target.value } : r))}
                              style={{ width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                            />
                            {dayLabel && (
                              <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:'0.65rem', fontWeight:700, color:'#6366f1', pointerEvents:'none' }}>
                                {dayLabel}
                              </span>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="e.g. Republic Day"
                            value={h.name}
                            onChange={e => setHolidaysList(p => p.map((r, i) => i===idx ? { ...r, name: e.target.value } : r))}
                            style={{ width:'100%', padding:'8px 10px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:'0.875rem', color:'#0f172a', boxSizing:'border-box', outline:'none' }}
                          />
                          <button type="button" onClick={() => setHolidaysList(p => p.filter((_,i) => i!==idx))}
                            title="Remove"
                            style={{ width:32, height:32, borderRadius:7, border:'1px solid #fca5a5',
                              background:'#fff1f2', color:'#dc2626', cursor:'pointer',
                              display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ MY EMPLOYMENT TAB ══════════════════════════════ */}
        {tab === 'my_employment' && (
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            {myProfileLoading ? (
              <div style={{ textAlign:'center', color:'#94a3b8', padding:'60px 0', fontSize:'0.9rem' }}>Loading…</div>
            ) : !myProfile ? (
              <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, padding:'40px 32px', textAlign:'center' }}>
                <div style={{ fontSize:'2rem', marginBottom:12 }}>👤</div>
                <div style={{ fontWeight:700, color:'#0f172a', marginBottom:8 }}>No Employee Record Linked</div>
                <div style={{ color:'#64748b', fontSize:'0.88rem' }}>Your user account is not yet linked to an employee profile. Please contact HR to set this up.</div>
              </div>
            ) : (
              <>
                {/* Profile card */}
                <div style={{ background:'linear-gradient(135deg,#1e3a5f,#1d4ed8)', borderRadius:16, padding:'28px 32px', color:'#fff', marginBottom:20 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:18, flexWrap:'wrap' }}>
                    <Avatar name={`${myProfile.first_name} ${myProfile.last_name||''}`} size={64} />
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ fontSize:'1.3rem', fontWeight:900 }}>{myProfile.first_name} {myProfile.last_name}</div>
                      <div style={{ fontSize:'0.9rem', opacity:0.85 }}>{myProfile.designation || '—'} · {myProfile.department || '—'}</div>
                      <div style={{ fontSize:'0.78rem', opacity:0.7, marginTop:4 }}>
                        {myProfile.employment_type?.replace('_',' ')} · Joined {fmtDate(myProfile.joining_date)}
                        {myProfile.confirmation_date ? ` · Confirmed ${fmtDate(myProfile.confirmation_date)}` : ''}
                      </div>
                    </div>
                    <div>
                      {(() => { const m = STATUS_META[myProfile.status]||STATUS_META.active; return <Badge text={m.label} bg={m.bg} color={m.color} />; })()}
                    </div>
                  </div>
                </div>

                {/* Info grid */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                  <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'16px 20px' }}>
                    <SectionHead title="Contact" />
                    <InfoRow label="Email" value={myProfile.email} />
                    <InfoRow label="Phone" value={myProfile.phone} />
                    <InfoRow label="City" value={myProfile.city} />
                    <InfoRow label="Work Location" value={myProfile.work_location} />
                  </div>
                  <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'16px 20px' }}>
                    <SectionHead title="Personal" />
                    <InfoRow label="Date of Birth" value={fmtDate(myProfile.dob)} />
                    <InfoRow label="Gender" value={myProfile.gender} />
                    <InfoRow label="Blood Group" value={myProfile.blood_group} />
                    <InfoRow label="Emergency" value={myProfile.emergency_name ? `${myProfile.emergency_name} (${myProfile.emergency_relation}) ${myProfile.emergency_phone}` : null} />
                  </div>
                </div>

                {/* Career Timeline */}
                <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, padding:'20px 24px', marginBottom:20 }}>
                  <div style={{ fontSize:'0.72rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:20 }}>
                    Career Timeline
                  </div>
                  <CareerTimeline endpoint="/hr/me/timeline" />
                </div>

                {/* Payslips */}
                {myPayslips.length > 0 && (
                  <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:14, padding:'20px 24px' }}>
                    <div style={{ fontSize:'0.72rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>
                      My Payslips
                    </div>
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                        <thead>
                          <tr style={{ background:'#f8fafc' }}>
                            {['Month','Gross','Deductions','Net Pay','Status',''].map(h => (
                              <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'#475569', fontSize:'0.72rem', textTransform:'uppercase' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {myPayslips.map(p => (
                            <tr key={p.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                              <td style={{ padding:'10px 12px', fontWeight:600 }}>{p.payroll_month}</td>
                              <td style={{ padding:'10px 12px' }}>{fmtSalary(p.gross_earned)}</td>
                              <td style={{ padding:'10px 12px', color:'#dc2626' }}>
                                -{fmtSalary((Number(p.pf_employee)||0)+(Number(p.tds)||0)+(Number(p.pt)||0)+(Number(p.other_deductions)||0))}
                              </td>
                              <td style={{ padding:'10px 12px', fontWeight:700 }}>{fmtSalary(p.net_pay)}</td>
                              <td style={{ padding:'10px 12px' }}>
                                <span style={{ background: p.status==='paid'?'#d1fae5':'#fef3c7', color: p.status==='paid'?'#065f46':'#92400e', borderRadius:20, padding:'2px 10px', fontSize:'0.72rem', fontWeight:700 }}>
                                  {p.status}
                                </span>
                              </td>
                              <td style={{ padding:'10px 12px' }}>
                                <button onClick={async () => {
                                  try {
                                    const resp = await api.get(`/hr/me/payslips/${p.id}/download`, { responseType:'blob' });
                                    const url = URL.createObjectURL(resp.data);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = `payslip-${p.payroll_month}.pdf`;
                                    a.click(); URL.revokeObjectURL(url);
                                  } catch { alert('Failed to download payslip.'); }
                                }} style={{ padding:'5px 12px', border:'1px solid #e2e8f0', borderRadius:6, background:'#fff', color:'#374151', fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
                                  Download PDF
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          EMPLOYEE PROFILE VIEW MODAL
      ═══════════════════════════════════════════════════════ */}
      {viewEmp && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setViewEmp(null)}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:740, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            {/* Profile header */}
            <div style={{ background:'linear-gradient(135deg,#1e3a5f,#1d4ed8)', borderRadius:'20px 20px 0 0', padding:'28px 32px', color:'#fff' }}>
              <div style={{ display:'flex', alignItems:'center', gap:18 }}>
                <Avatar name={`${viewEmp.first_name} ${viewEmp.last_name||''}`} size={64} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'1.3rem', fontWeight:900 }}>{viewEmp.first_name} {viewEmp.last_name}</div>
                  <div style={{ fontSize:'0.9rem', opacity:0.85 }}>{viewEmp.designation || '—'} · {viewEmp.department || '—'}</div>
                  <div style={{ fontSize:'0.78rem', opacity:0.7, marginTop:4 }}>{viewEmp.employee_code} · Joined {fmtDate(viewEmp.joining_date)}</div>
                </div>
                <div style={{ display:'flex', gap:8, flexDirection:'column', alignItems:'flex-end' }}>
                  {(() => { const m = STATUS_META[viewEmp.status]||STATUS_META.active; return <Badge text={m.label} bg={m.bg} color={m.color} />; })()}
                  {(() => { const m = EMP_TYPE_META[viewEmp.employment_type]||EMP_TYPE_META.full_time; return <Badge text={m.label} bg={m.bg} color={m.color} />; })()}
                </div>
              </div>
            </div>

            <div style={{ padding:'24px 32px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
              {/* Contact */}
              <div>
                <SectionHead title="Contact Information" />
                <InfoRow label="Email" value={viewEmp.email} />
                <InfoRow label="Phone" value={viewEmp.phone} />
                <InfoRow label="Alternate" value={viewEmp.alternate_phone} />
                <InfoRow label="Address" value={[viewEmp.address, viewEmp.city, viewEmp.state, viewEmp.pincode, viewEmp.country].filter(Boolean).join(', ')} />
              </div>
              {/* Personal */}
              <div>
                <SectionHead title="Personal Details" />
                <InfoRow label="Date of Birth" value={fmtDate(viewEmp.dob)} />
                <InfoRow label="Gender" value={viewEmp.gender} />
                <InfoRow label="Blood Group" value={viewEmp.blood_group} />
                <InfoRow label="Nationality" value={viewEmp.nationality} />
                <InfoRow label="Marital Status" value={viewEmp.marital_status} />
              </div>
              {/* Employment */}
              <div>
                <SectionHead title="Employment" />
                <InfoRow label="Department" value={viewEmp.department} />
                <InfoRow label="Designation" value={viewEmp.designation} />
                <InfoRow label="Work Location" value={viewEmp.work_location} />
                <InfoRow label="Reports To" value={viewEmp.reporting_to_name} />
                <InfoRow label="Confirmation" value={fmtDate(viewEmp.confirmation_date)} />
              </div>
              {/* Compensation */}
              <div>
                <SectionHead title="Compensation & Documents" />
                <InfoRow label="Salary" value={fmtSalary(viewEmp.salary, viewEmp.salary_currency)} />
                <InfoRow label="Pay Frequency" value={viewEmp.pay_frequency?.replace('_',' ')} />
                <InfoRow label="PAN" value={viewEmp.pan_number} />
                <InfoRow label="Aadhaar" value={viewEmp.aadhaar_number ? '••••••••' + viewEmp.aadhaar_number.slice(-4) : '—'} />
              </div>
              {/* Salary Structure */}
              <div style={{ gridColumn:'1/-1' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <SectionHead title="Salary Structure" />
                  <button onClick={() => setShowSalaryEdit(v => !v)}
                    style={{ fontSize:'0.75rem', fontWeight:600, color:'#4f46e5', background:'#eef2ff', border:'1px solid #c7d2fe', borderRadius:6, padding:'4px 10px', cursor:'pointer' }}>
                    {showSalaryEdit ? 'Cancel' : salaryStruct ? 'Edit' : '+ Set Structure'}
                  </button>
                </div>
                {salaryStructMsg && (
                  <div style={{ padding:'8px 12px', borderRadius:6, marginBottom:10, fontSize:'0.8rem',
                    background: salaryStructMsg.type==='success'?'#d1fae5':'#fee2e2',
                    color: salaryStructMsg.type==='success'?'#065f46':'#dc2626' }}>
                    {salaryStructMsg.text}
                  </div>
                )}
                {showSalaryEdit ? (
                  <div style={{ background:'#f8fafc', borderRadius:10, padding:'16px', border:'1px solid #e2e8f0' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px 16px', marginBottom:14 }}>
                      {[['basic','Basic'],['hra','HRA'],['da','DA'],['ta','TA'],['special_allowance','Special Allowance'],['other_allowance','Other Allowance']].map(([k,l]) => (
                        <div key={k}>
                          <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>{l} (₹/mo)</label>
                          <input type="number" min="0" step="100"
                            value={salaryStructForm[k]}
                            onChange={e => setSalaryStructForm(p => ({ ...p, [k]: e.target.value }))}
                            style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ borderTop:'1px solid #e2e8f0', paddingTop:12, marginBottom:12 }}>
                      <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 }}>Deductions</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'10px 16px' }}>
                        <div>
                          <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>PF Employee %</label>
                          <input type="number" min="0" max="100" step="0.5"
                            value={salaryStructForm.pf_employee_pct}
                            onChange={e => setSalaryStructForm(p => ({ ...p, pf_employee_pct: e.target.value }))}
                            style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>PF Employer %</label>
                          <input type="number" min="0" max="100" step="0.5"
                            value={salaryStructForm.pf_employer_pct}
                            onChange={e => setSalaryStructForm(p => ({ ...p, pf_employer_pct: e.target.value }))}
                            style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>PT (₹/mo)</label>
                          <input type="number" min="0" step="10"
                            value={salaryStructForm.pt_monthly}
                            onChange={e => setSalaryStructForm(p => ({ ...p, pt_monthly: e.target.value }))}
                            style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>TDS (₹/mo)</label>
                          <input type="number" min="0" step="100"
                            value={salaryStructForm.tds_monthly}
                            onChange={e => setSalaryStructForm(p => ({ ...p, tds_monthly: e.target.value }))}
                            style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                        </div>
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 16px', marginBottom:14 }}>
                      <div>
                        <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Effective From</label>
                        <input type="date" value={salaryStructForm.effective_from}
                          onChange={e => setSalaryStructForm(p => ({ ...p, effective_from: e.target.value }))}
                          style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Notes</label>
                        <input type="text" value={salaryStructForm.notes}
                          onChange={e => setSalaryStructForm(p => ({ ...p, notes: e.target.value }))}
                          style={{ width:'100%', padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:'0.83rem', boxSizing:'border-box' }} />
                      </div>
                    </div>
                    {/* Live summary */}
                    {(() => {
                      const gross = ['basic','hra','da','ta','special_allowance','other_allowance']
                        .reduce((s,k) => s + (parseFloat(salaryStructForm[k])||0), 0);
                      const pfEmp = (parseFloat(salaryStructForm.basic)||0) * (v => { const n = parseFloat(v); return Number.isNaN(n) ? 12 : n; })(salaryStructForm.pf_employee_pct) / 100;
                      const pt  = parseFloat(salaryStructForm.pt_monthly) || 0;
                      const tds = parseFloat(salaryStructForm.tds_monthly) || 0;
                      const net = gross - pfEmp - pt - tds;
                      return (
                        <div style={{ display:'flex', gap:16, padding:'10px 14px', background:'#1e3a5f', borderRadius:8, color:'#fff', fontSize:'0.8rem', flexWrap:'wrap' }}>
                          <span>Gross: <strong>₹{gross.toLocaleString('en-IN', {maximumFractionDigits:0})}</strong></span>
                          <span style={{ opacity:0.6 }}>|</span>
                          <span>Deductions: <strong style={{ color:'#fca5a5' }}>₹{(pfEmp+pt+tds).toLocaleString('en-IN', {maximumFractionDigits:0})}</strong></span>
                          <span style={{ opacity:0.6 }}>|</span>
                          <span>Net: <strong style={{ color:'#6ee7b7' }}>₹{net.toLocaleString('en-IN', {maximumFractionDigits:0})}</strong></span>
                        </div>
                      );
                    })()}
                    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
                      <button onClick={() => saveSalaryStruct(viewEmp.id)} disabled={salaryStructSaving}
                        style={{ padding:'8px 20px', border:'none', borderRadius:7, background:'#1d4ed8', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor:'pointer', opacity: salaryStructSaving ? 0.7 : 1 }}>
                        {salaryStructSaving ? 'Saving...' : 'Save Structure'}
                      </button>
                    </div>
                  </div>
                ) : salaryStruct ? (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
                    {[['Basic','basic'],['HRA','hra'],['DA','da'],['TA','ta'],['Special','special_allowance'],['Other','other_allowance']].map(([l,k]) => (
                      <div key={k} style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', border:'1px solid #e2e8f0' }}>
                        <div style={{ fontSize:'0.7rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase', marginBottom:3 }}>{l}</div>
                        <div style={{ fontWeight:700, color:'#0f172a' }}>₹{parseFloat(salaryStruct[k]||0).toLocaleString('en-IN')}</div>
                      </div>
                    ))}
                    <div style={{ gridColumn:'1/-1', display:'flex', gap:16, padding:'10px 14px', background:'#0f172a', borderRadius:8, color:'#fff', fontSize:'0.82rem', flexWrap:'wrap' }}>
                      {(() => {
                        const gross = ['basic','hra','da','ta','special_allowance','other_allowance'].reduce((s,k) => s+(parseFloat(salaryStruct[k])||0), 0);
                        const pfEmp = (parseFloat(salaryStruct.basic)||0) * (v => { const n = parseFloat(v); return Number.isNaN(n) ? 12 : n; })(salaryStruct.pf_employee_pct)/100;
                        const pt  = parseFloat(salaryStruct.pt_monthly)||0;
                        const tds = parseFloat(salaryStruct.tds_monthly)||0;
                        const net = gross - pfEmp - pt - tds;
                        return (<>
                          <span>Gross: <strong>₹{gross.toLocaleString('en-IN',{maximumFractionDigits:0})}</strong></span>
                          <span style={{opacity:0.4}}>|</span>
                          <span>PF: <strong style={{color:'#fca5a5'}}>₹{pfEmp.toLocaleString('en-IN',{maximumFractionDigits:0})}</strong></span>
                          <span>PT: <strong style={{color:'#fca5a5'}}>₹{pt.toLocaleString('en-IN')}</strong></span>
                          <span>TDS: <strong style={{color:'#fca5a5'}}>₹{tds.toLocaleString('en-IN')}</strong></span>
                          <span style={{opacity:0.4}}>|</span>
                          <span>Net/mo: <strong style={{color:'#6ee7b7',fontSize:'0.9rem'}}>₹{net.toLocaleString('en-IN',{maximumFractionDigits:0})}</strong></span>
                        </>);
                      })()}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding:'16px', background:'#fef3c7', borderRadius:8, color:'#92400e', fontSize:'0.83rem', border:'1px solid #fde68a' }}>
                    No salary structure defined. Click "+ Set Structure" to configure allowances and deductions.
                  </div>
                )}
              </div>
              {/* Emergency */}
              <div>
                <SectionHead title="Emergency Contact" />
                <InfoRow label="Name" value={viewEmp.emergency_name} />
                <InfoRow label="Relation" value={viewEmp.emergency_relation} />
                <InfoRow label="Phone" value={viewEmp.emergency_phone} />
              </div>
              {/* Bank & Payment */}
              <div>
                <SectionHead title="Bank & Payment Details" />
                <InfoRow label="Bank" value={viewEmp.bank_name} />
                <InfoRow label="Account No." value={viewEmp.bank_account ? '••••' + viewEmp.bank_account.slice(-4) : undefined} />
                <InfoRow label="IFSC" value={viewEmp.bank_ifsc} />
                <InfoRow label="Branch" value={viewEmp.bank_branch} />
                <InfoRow label="UPI ID" value={viewEmp.upi_id} />
                {viewEmp.payment_notes && <InfoRow label="Notes" value={viewEmp.payment_notes} />}
              </div>
              {/* Notes */}
              {viewEmp.notes && (
                <div>
                  <SectionHead title="Notes" />
                  <div style={{ fontSize:'0.83rem', color:'#374151', lineHeight:1.6 }}>{viewEmp.notes}</div>
                </div>
              )}
            </div>

            {/* Recent leaves */}
            {viewEmp.leaves?.length > 0 && (
              <div style={{ padding:'0 32px 24px' }}>
                <SectionHead title="Recent Leave History" />
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {viewEmp.leaves.slice(0,5).map(l => {
                    const lm = LEAVE_STATUS[l.status]||LEAVE_STATUS.pending;
                    return (
                      <div key={l.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', background:'#f8fafc', borderRadius:8 }}>
                        <div>
                          <span style={{ fontSize:'0.82rem', fontWeight:600, color:'#374151', textTransform:'capitalize' }}>{l.leave_type.replace('_',' ')}</span>
                          <span style={{ fontSize:'0.75rem', color:'#94a3b8', marginLeft:10 }}>{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</span>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#0f172a' }}>{l.days} day{l.days!==1?'s':''}</span>
                          <Badge text={lm.label} bg={lm.bg} color={lm.color} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Pay Revisions ── */}
            <div style={{ padding:'0 32px 28px' }}>
              <div style={{ borderTop:'2px solid #eff6ff', paddingTop:18, marginBottom:14 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>
                  Pay Revision History
                </div>
                {/* Add revision form */}
                <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:14, marginBottom:14 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>Effective Date *</div>
                      <input type="date" style={inp} value={payRevForm.effective_date}
                        onChange={e => setPayRevForm(p => ({ ...p, effective_date: e.target.value }))} />
                    </div>
                    <div>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>Revision Type</div>
                      <select style={sel} value={payRevForm.revision_type}
                        onChange={e => setPayRevForm(p => ({ ...p, revision_type: e.target.value }))}>
                        {Object.entries(PAY_REV_TYPES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>Currency</div>
                      <select style={sel} value={payRevForm.currency}
                        onChange={e => setPayRevForm(p => ({ ...p, currency: e.target.value }))}>
                        {['INR','USD','EUR','GBP','AED'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:10, marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>New Salary (Annual) *</div>
                      <input type="number" min="0" style={inp} placeholder="e.g. 600000"
                        value={payRevForm.new_salary}
                        onChange={e => setPayRevForm(p => ({ ...p, new_salary: e.target.value }))} />
                    </div>
                    <div>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>Notes / Reason</div>
                      <input style={inp} placeholder="e.g. Annual appraisal 2026"
                        value={payRevForm.notes}
                        onChange={e => setPayRevForm(p => ({ ...p, notes: e.target.value }))} />
                    </div>
                  </div>
                  {payRevError && <div style={{ color:'#dc2626', fontSize:'0.78rem', marginBottom:8 }}>{payRevError}</div>}
                  <button onClick={handleAddPayRevision} disabled={payRevSaving}
                    style={{ padding:'7px 18px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.82rem', fontWeight:700, cursor:'pointer' }}>
                    {payRevSaving ? 'Saving…' : '+ Record Revision'}
                  </button>
                </div>
                {/* History table */}
                {payRevisions.length === 0 ? (
                  <div style={{ textAlign:'center', color:'#94a3b8', fontSize:'0.83rem', padding:'12px 0' }}>No pay revisions recorded yet.</div>
                ) : (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
                      <thead>
                        <tr style={{ background:'#f1f5f9' }}>
                          {['Effective Date','Type','Old Salary','New Salary','Change %','Notes','By',''].map(h => (
                            <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:700, color:'#475569', fontSize:'0.72rem', textTransform:'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {payRevisions.map(r => (
                          <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                            <td style={{ padding:'8px 10px', fontWeight:600 }}>{fmtDate(r.effective_date)}</td>
                            <td style={{ padding:'8px 10px' }}>
                              <span style={{ background:'#dbeafe', color:'#1e40af', borderRadius:6, padding:'2px 8px', fontSize:'0.72rem', fontWeight:700 }}>
                                {PAY_REV_TYPES[r.revision_type] || r.revision_type}
                              </span>
                            </td>
                            <td style={{ padding:'8px 10px', color:'#64748b' }}>{r.old_salary ? `${r.currency} ${Number(r.old_salary).toLocaleString()}` : '—'}</td>
                            <td style={{ padding:'8px 10px', fontWeight:700, color:'#065f46' }}>{r.currency} {Number(r.new_salary).toLocaleString()}</td>
                            <td style={{ padding:'8px 10px' }}>
                              {r.percentage_change != null ? (
                                <span style={{ color: r.percentage_change >= 0 ? '#16a34a' : '#dc2626', fontWeight:700 }}>
                                  {r.percentage_change >= 0 ? '+' : ''}{Number(r.percentage_change).toFixed(1)}%
                                </span>
                              ) : '—'}
                            </td>
                            <td style={{ padding:'8px 10px', color:'#64748b', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.notes || '—'}</td>
                            <td style={{ padding:'8px 10px', color:'#94a3b8', fontSize:'0.75rem' }}>{r.recorded_by_name || '—'}</td>
                            <td style={{ padding:'8px 10px' }}>
                              <button onClick={() => handleDeletePayRevision(r.id)}
                                style={{ border:'none', background:'none', cursor:'pointer', color:'#ef4444', fontSize:'0.85rem', padding:'2px 6px' }}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── Employee Documents ── */}
              <div style={{ borderTop:'2px solid #eff6ff', paddingTop:18 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>
                  Documents
                </div>
                <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:14, marginBottom:14 }}>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
                    <div style={{ minWidth:160 }}>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>Document Type</div>
                      <select style={sel} value={empDocForm.doc_type}
                        onChange={e => setEmpDocForm(p => ({ ...p, doc_type: e.target.value, doc_description:'' }))}>
                        {Object.entries(EMP_DOC_TYPES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    {empDocForm.doc_type === 'others' && (
                      <div style={{ flex:1, minWidth:130 }}>
                        <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>Describe Type *</div>
                        <input style={inp} placeholder="e.g. Medical Certificate…"
                          value={empDocForm.doc_description}
                          onChange={e => setEmpDocForm(p => ({ ...p, doc_description: e.target.value }))} />
                      </div>
                    )}
                    <div style={{ flex:2, minWidth:180 }}>
                      <div style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', marginBottom:4 }}>File</div>
                      <input type="file" style={inp} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt"
                        onChange={e => setEmpDocForm(p => ({ ...p, file: e.target.files[0] || null }))} />
                    </div>
                    <button onClick={handleEmpDocUpload} disabled={empDocUploading}
                      style={{ padding:'8px 18px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                      {empDocUploading ? 'Uploading…' : 'Attach'}
                    </button>
                  </div>
                  {empDocError && <div style={{ color:'#dc2626', fontSize:'0.78rem', marginTop:8 }}>{empDocError}</div>}
                </div>
                {empDocs.length === 0 ? (
                  <div style={{ textAlign:'center', color:'#94a3b8', fontSize:'0.83rem', padding:'10px 0' }}>No documents attached yet.</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {empDocs.map(doc => (
                      <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8 }}>
                        <span style={{ fontSize:'1.15rem' }}>{empDocIcon(doc.original_name)}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:600, fontSize:'0.83rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{doc.original_name}</div>
                          <div style={{ fontSize:'0.72rem', color:'#94a3b8' }}>
                            <span style={{ fontWeight:700, color:'#1d4ed8', textTransform:'uppercase' }}>
                              {doc.doc_type === 'others' && doc.doc_description ? doc.doc_description : (EMP_DOC_TYPES[doc.doc_type] || doc.doc_type)}
                            </span>
                            {' · '}{doc.uploaded_by_name}
                            {' · '}{fmtDate(doc.created_at)}
                            {doc.file_size ? ` · ${(doc.file_size/1024).toFixed(1)} KB` : ''}
                          </div>
                        </div>
                        <button onClick={() => handleEmpDocDownload(doc.id, doc.original_name)}
                          style={{ border:'1px solid #e2e8f0', background:'#fff', borderRadius:6, padding:'5px 10px', cursor:'pointer', color:'#374151', fontSize:'0.78rem', fontWeight:600 }}>
                          ↓ Download
                        </button>
                        <button onClick={() => handleEmpDocDelete(doc.id)}
                          style={{ border:'none', background:'none', cursor:'pointer', color:'#ef4444', fontSize:'0.85rem', padding:'2px 6px' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Career Timeline (admin/manager view) ── */}
            <div style={{ padding:'0 32px 28px' }}>
              <div style={{ borderTop:'2px solid #eff6ff', paddingTop:18 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:20 }}>
                  Career Timeline
                </div>
                <CareerTimeline endpoint={`/hr/${viewEmp.id}/timeline`} />
              </div>
            </div>

            {!viewEmp.is_active && (
              <div style={{ margin:'0 32px 16px', padding:'12px 16px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8 }}>
                <div style={{ fontWeight:700, color:'#dc2626', fontSize:'0.875rem' }}>⛔ Record Frozen</div>
                <div style={{ color:'#991b1b', fontSize:'0.8rem', marginTop:2 }}>This employee record is frozen and cannot be edited.</div>
              </div>
            )}
            <div style={{ padding:'16px 32px 24px', display:'flex', justifyContent:'flex-end', gap:10, borderTop:'1px solid #f1f5f9' }}>
              {!viewEmp.is_active
                ? isAdmin && <button onClick={() => handleUnfreeze(viewEmp.id)}
                    style={{ padding:'9px 20px', border:'none', borderRadius:8, background:'#16a34a', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor:'pointer' }}>
                    Unfreeze
                  </button>
                : <button onClick={() => { setViewEmp(null); openEdit(viewEmp); }}
                    style={{ padding:'9px 20px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', color:'#374151', fontSize:'0.83rem', fontWeight:600, cursor:'pointer' }}>
                    Edit Profile
                  </button>
              }
              <button onClick={() => setViewEmp(null)}
                style={{ padding:'9px 20px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor:'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          ADD / EDIT EMPLOYEE MODAL
      ═══════════════════════════════════════════════════════ */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setModal(false)}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:780, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 25px 50px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'24px 28px 16px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h2 style={{ fontSize:'1.1rem', fontWeight:900, color:'#0f172a' }}>{form.id ? 'Edit Employee' : 'Add New Employee'}</h2>
              <button onClick={() => setModal(false)} style={{ border:'none', background:'none', cursor:'pointer', fontSize:'1.4rem', color:'#94a3b8', lineHeight:1 }}>×</button>
            </div>

            <div style={{ padding:'20px 28px' }}>
              {error && <div style={{ background:'#fee2e2', color:'#dc2626', borderRadius:8, padding:'8px 14px', marginBottom:16, fontSize:'0.82rem' }}>{error}</div>}

              {/* ── Personal Info ─────────────────────────── */}
              <SectionHead title="Personal Information" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
                <Field label={<span>Employee Code{!form.id && <span style={{ marginLeft:6, fontSize:'0.7rem', fontWeight:400, color:'#6366f1', background:'#eef2ff', padding:'1px 6px', borderRadius:4 }}>auto</span>}</span>}>
                  <input style={inp} value={form.employee_code} onChange={e => f('employee_code',e.target.value)} placeholder="Auto-generated" />
                </Field>
                <Field label="Status">
                  <select style={sel} value={form.status} onChange={e => f('status',e.target.value)}>
                    {Object.entries(STATUS_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </Field>
                <Field label="First Name *"><input style={inp} value={form.first_name} onChange={e => f('first_name',e.target.value)} /></Field>
                <Field label="Last Name"><input style={inp} value={form.last_name} onChange={e => f('last_name',e.target.value)} /></Field>
                <Field label="Email *"><input style={inp} type="email" value={form.email} onChange={e => f('email',e.target.value)} /></Field>
                <Field label="Phone"><input style={inp} value={form.phone} onChange={e => f('phone',e.target.value)} /></Field>
                <Field label="Alternate Phone"><input style={inp} value={form.alternate_phone} onChange={e => f('alternate_phone',e.target.value)} /></Field>
                <Field label="Date of Birth"><input style={inp} type="date" value={form.dob} onChange={e => f('dob',e.target.value)} /></Field>
                <Field label="Gender">
                  <select style={sel} value={form.gender} onChange={e => f('gender',e.target.value)}>
                    <option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Blood Group"><input style={inp} value={form.blood_group} onChange={e => f('blood_group',e.target.value)} placeholder="A+, B-, O+ …" /></Field>
                <Field label="Nationality"><input style={inp} value={form.nationality} onChange={e => f('nationality',e.target.value)} /></Field>
                <Field label="Marital Status">
                  <select style={sel} value={form.marital_status} onChange={e => f('marital_status',e.target.value)}>
                    <option value="">Select</option><option value="single">Single</option><option value="married">Married</option><option value="divorced">Divorced</option><option value="widowed">Widowed</option>
                  </select>
                </Field>
              </div>

              {/* ── Address ──────────────────────────────── */}
              <SectionHead title="Address" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <Field label="Street Address"><input style={inp} value={form.address} onChange={e => f('address',e.target.value)} /></Field>
                </div>
                <Field label="City">
                  {form.country === 'India' && form.state && MAJOR_CITIES[form.state] ? (
                    <select style={sel} value={form.city} onChange={e => f('city', e.target.value)}>
                      <option value="">-- Select City --</option>
                      {MAJOR_CITIES[form.state].map(city => <option key={city} value={city}>{city}</option>)}
                    </select>
                  ) : (
                    <input style={inp} value={form.city} onChange={e => f('city', e.target.value)} placeholder="Enter city" />
                  )}
                </Field>
                <Field label="Pincode"><input style={inp} value={form.pincode} onChange={e => f('pincode',e.target.value)} /></Field>
                <Field label="Country">
                  <select style={sel} value={form.country} 
                    onChange={e => {
                      const val = e.target.value;
                      f('country', val);
                      if (val !== 'India') f('state', '');
                    }}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="State">
                  {form.country === 'India' ? (
                    <select style={sel} value={form.state} onChange={e => f('state', e.target.value)}>
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(s => <option key={s.code} value={s.name}>{s.name}</option>)}
                    </select>
                  ) : (
                    <input style={inp} value={form.state} onChange={e => f('state', e.target.value)} placeholder="Enter state" />
                  )}
                </Field>
              </div>

              {/* ── Emergency Contact ─────────────────────── */}
              <SectionHead title="Emergency Contact" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
                <Field label="Name"><input style={inp} value={form.emergency_name} onChange={e => f('emergency_name',e.target.value)} /></Field>
                <Field label="Relation"><input style={inp} value={form.emergency_relation} onChange={e => f('emergency_relation',e.target.value)} placeholder="Spouse, Parent…" /></Field>
                <Field label="Phone"><input style={inp} value={form.emergency_phone} onChange={e => f('emergency_phone',e.target.value)} /></Field>
              </div>

              {/* ── Employment ───────────────────────────── */}
              <SectionHead title="Employment Details" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
                <Field label="Department">
                  <select style={sel} value={form.department} onChange={e => f('department',e.target.value)}>
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Designation">
                  <DesignationPicker value={form.designation} onChange={v => f('designation', v)} />
                </Field>
                <Field label="Employment Type">
                  <select style={sel} value={form.employment_type} onChange={e => f('employment_type',e.target.value)}>
                    {Object.entries(EMP_TYPE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </Field>
                <Field label="Work Location"><input style={inp} value={form.work_location} onChange={e => f('work_location',e.target.value)} /></Field>
                <Field label="Joining Date"><input style={inp} type="date" value={form.joining_date} onChange={e => f('joining_date',e.target.value)} /></Field>
                <Field label="Confirmation Date"><input style={inp} type="date" value={form.confirmation_date} onChange={e => f('confirmation_date',e.target.value)} /></Field>
                <Field label="Reporting To">
                  <select style={sel} value={form.reporting_to} onChange={e => f('reporting_to',e.target.value)}>
                    <option value="">None</option>
                    {allEmps.filter(e => e.id !== form.id).map(e => (
                      <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.designation||e.department}</option>
                    ))}
                  </select>
                </Field>
                {(form.status === 'resigned' || form.status === 'terminated') && (
                  <Field label="Exit Date"><input style={inp} type="date" value={form.exit_date} onChange={e => f('exit_date',e.target.value)} /></Field>
                )}
                {(form.status === 'resigned' || form.status === 'terminated') && (
                  <div style={{ gridColumn:'1/-1' }}>
                    <Field label="Exit Reason"><input style={inp} value={form.exit_reason} onChange={e => f('exit_reason',e.target.value)} /></Field>
                  </div>
                )}
              </div>

              {/* ── Compensation ─────────────────────────── */}
              <SectionHead title="Compensation & Documents" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
                <Field label="Annual Salary"><input style={inp} type="number" value={form.salary} onChange={e => f('salary',e.target.value)} placeholder="e.g. 600000" /></Field>
                <Field label="Currency">
                  <select style={sel} value={form.salary_currency} onChange={e => f('salary_currency',e.target.value)}>
                    <option value="INR">INR (₹)</option><option value="USD">USD ($)</option><option value="EUR">EUR (€)</option>
                  </select>
                </Field>
                <Field label="Pay Frequency">
                  <select style={sel} value={form.pay_frequency} onChange={e => f('pay_frequency',e.target.value)}>
                    <option value="monthly">Monthly</option><option value="weekly">Weekly</option><option value="bi_weekly">Bi-Weekly</option>
                  </select>
                </Field>
                <Field label="PAN Number"><input style={inp} value={form.pan_number} onChange={e => f('pan_number',e.target.value)} placeholder="ABCDE1234F" /></Field>
                <Field label="Aadhaar Number"><input style={inp} value={form.aadhaar_number} onChange={e => f('aadhaar_number',e.target.value)} placeholder="xxxx xxxx xxxx" /></Field>
              </div>

              {/* ── Bank & Payment Details ────────────────── */}
              <SectionHead title="Bank & Payment Details" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px 20px' }}>
                <Field label="Bank Name"><input style={inp} value={form.bank_name} onChange={e => f('bank_name',e.target.value)} placeholder="e.g. HDFC Bank" /></Field>
                <Field label="Account Number"><input style={inp} value={form.bank_account} onChange={e => f('bank_account',e.target.value)} placeholder="e.g. 50100123456789" /></Field>
                <Field label="IFSC Code"><input style={inp} value={form.bank_ifsc} onChange={e => f('bank_ifsc',e.target.value)} placeholder="e.g. HDFC0001234" /></Field>
                <Field label="Branch"><input style={inp} value={form.bank_branch} onChange={e => f('bank_branch',e.target.value)} placeholder="e.g. Kukatpally" /></Field>
                <Field label="UPI ID"><input style={inp} value={form.upi_id} onChange={e => f('upi_id',e.target.value)} placeholder="e.g. name@upi" /></Field>
                <Field label="Payment Notes"><input style={inp} value={form.payment_notes} onChange={e => f('payment_notes',e.target.value)} placeholder="Any salary disbursement note" /></Field>
              </div>

              {/* ── Notes ────────────────────────────────── */}
              <SectionHead title="Notes" />
              <Field label="Internal Notes">
                <textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={form.notes} onChange={e => f('notes',e.target.value)} />
              </Field>
            </div>

            <div style={{ padding:'16px 28px 24px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={() => setModal(false)} style={{ padding:'9px 20px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', color:'#374151', fontSize:'0.83rem', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding:'9px 24px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : form.id ? 'Update Employee' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Leave Request Modal ───────────────────────────────── */}
      {leaveModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setLM(false)}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:480, boxShadow:'0 20px 40px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'20px 24px 14px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h2 style={{ fontSize:'1rem', fontWeight:800, color:'#0f172a' }}>New Leave Request</h2>
              <button onClick={() => setLM(false)} style={{ border:'none', background:'none', cursor:'pointer', fontSize:'1.4rem', color:'#94a3b8' }}>×</button>
            </div>
            <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:14 }}>
              {error && <div style={{ background:'#fee2e2', color:'#dc2626', borderRadius:8, padding:'8px 12px', fontSize:'0.82rem' }}>{error}</div>}
              <Field label="Employee *">
                <select style={sel} value={leaveForm.employee_id} onChange={e => setLF2(p => ({...p, employee_id:e.target.value}))}>
                  <option value="">Select employee</option>
                  {allEmps.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
                </select>
              </Field>
              <Field label="Leave Type">
                <select style={sel} value={leaveForm.leave_type} onChange={e => setLF2(p => ({...p, leave_type:e.target.value}))}>
                  {LEAVE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1).replace('_',' ')}</option>)}
                </select>
              </Field>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <Field label="From *"><input style={inp} type="date" value={leaveForm.from_date} onChange={e => setLF2(p => ({...p, from_date:e.target.value}))} /></Field>
                <Field label="To *"><input style={inp} type="date" value={leaveForm.to_date} onChange={e => setLF2(p => ({...p, to_date:e.target.value}))} /></Field>
              </div>
              <Field label="Number of Days *"><input style={inp} type="number" min="0.5" step="0.5" value={leaveForm.days} onChange={e => setLF2(p => ({...p, days:e.target.value}))} /></Field>
              <Field label="Reason"><textarea style={{ ...inp, minHeight:70, resize:'vertical' }} value={leaveForm.reason} onChange={e => setLF2(p => ({...p, reason:e.target.value}))} /></Field>
            </div>
            <div style={{ padding:'12px 24px 20px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={() => setLM(false)} style={{ padding:'8px 18px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', color:'#374151', fontSize:'0.83rem', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleLeaveSubmit} disabled={saving} style={{ padding:'8px 20px', border:'none', borderRadius:8, background:'#1d4ed8', color:'#fff', fontSize:'0.83rem', fontWeight:700, cursor: saving?'default':'pointer', opacity:saving?0.7:1 }}>
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ───────────────────────────────────── */}
      {deleteId && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setDelId(null)}>
          <div style={{ background:'#fff', borderRadius:14, padding:'28px 32px', width:360, textAlign:'center', boxShadow:'0 20px 40px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'2rem', marginBottom:12 }}>🔒</div>
            <div style={{ fontWeight:800, fontSize:'1rem', color:'#0f172a', marginBottom:8 }}>Freeze Employee?</div>
            <div style={{ fontSize:'0.83rem', color:'#64748b', marginBottom:24 }}>This will freeze the employee record, locking it from editing. You can unfreeze it later.</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button onClick={() => setDelId(null)} style={{ padding:'9px 20px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleDelete} style={{ padding:'9px 20px', border:'none', borderRadius:8, background:'#dc2626', color:'#fff', fontWeight:700, cursor:'pointer' }}>Freeze</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Org Chart ──────────────────────────────────────────────────── */
function OrgChart({ employees }) {
  const [highlight, setHighlight] = React.useState(null);
  const [selected, setSelected] = React.useState(null);

  // Build id→emp map and children map
  const byId = {};
  employees.forEach(e => { byId[e.id] = e; });

  const children = {};
  employees.forEach(e => {
    const pid = e.reporting_to || null;
    if (!children[pid]) children[pid] = [];
    children[pid].push(e);
  });

  // Roots: no reporting_to, or reporting_to points to a non-existent employee
  const roots = employees.filter(e => !e.reporting_to || !byId[e.reporting_to]);

  if (employees.length === 0) {
    return <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>No employees found.</div>;
  }

  const statusDot = { active:'#10b981', on_leave:'#f59e0b', on_notice:'#f97316', resigned:'#ef4444', terminated:'#6b7280' };

  // Build upward chain (ancestors) for an employee
  function getAncestors(empId) {
    const chain = [];
    let cur = byId[empId];
    while (cur && cur.reporting_to && byId[cur.reporting_to]) {
      cur = byId[cur.reporting_to];
      chain.unshift(cur);
    }
    return chain;
  }

  function EmpPill({ emp, role }) {
    const hue = ((emp.first_name || '').charCodeAt(0) || 65) * 17 % 360;
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#f8fafc', borderRadius:8, border:'1px solid #e2e8f0' }}>
        <div style={{
          width:32, height:32, borderRadius:'50%', flexShrink:0,
          background:`hsl(${hue},50%,88%)`, color:`hsl(${hue},50%,28%)`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontWeight:700, fontSize:'0.78rem',
        }}>
          {`${emp.first_name[0]}${emp.last_name?.[0]||''}`.toUpperCase()}
        </div>
        <div style={{ minWidth:0 }}>
          <div style={{ fontWeight:600, fontSize:'0.82rem', color:'#0f172a' }}>{emp.first_name} {emp.last_name || ''}</div>
          {emp.designation && <div style={{ fontSize:'0.7rem', color:'#1d4ed8', fontWeight:500 }}>{emp.designation}</div>}
          {role && <div style={{ fontSize:'0.65rem', color:'#94a3b8', marginTop:1 }}>{role}</div>}
        </div>
        <div style={{ marginLeft:'auto', width:7, height:7, borderRadius:'50%', background: statusDot[emp.status]||'#6b7280', flexShrink:0 }} title={emp.status} />
      </div>
    );
  }

  // Hierarchy panel shown on right when employee selected
  function HierarchyPanel({ emp }) {
    const ancestors = getAncestors(emp.id);
    const directReports = children[emp.id] || [];
    const hue = ((emp.first_name || '').charCodeAt(0) || 65) * 17 % 360;

    return (
      <div style={{
        position:'sticky', top:0, alignSelf:'flex-start',
        width:280, flexShrink:0,
        background:'#fff', border:'1px solid #e2e8f0', borderRadius:14,
        boxShadow:'0 4px 24px rgba(0,0,0,0.10)', padding:20,
        maxHeight:'80vh', overflowY:'auto',
      }}>
        {/* Close */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <span style={{ fontWeight:700, fontSize:'0.85rem', color:'#0f172a' }}>Reporting Hierarchy</span>
          <button onClick={() => { setSelected(null); setHighlight(null); }}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:'1rem', lineHeight:1, padding:2 }}>✕</button>
        </div>

        {/* Selected employee */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:'0.65rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Selected</div>
          <div style={{ padding:'10px 14px', background:'#eff6ff', borderRadius:10, border:'2px solid #1d4ed8', textAlign:'center' }}>
            <div style={{
              width:44, height:44, borderRadius:'50%', margin:'0 auto 8px',
              background:`hsl(${hue},50%,88%)`, color:`hsl(${hue},50%,28%)`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:700, fontSize:'1rem',
            }}>
              {`${emp.first_name[0]}${emp.last_name?.[0]||''}`.toUpperCase()}
            </div>
            <div style={{ fontWeight:700, fontSize:'0.85rem', color:'#0f172a' }}>{emp.first_name} {emp.last_name||''}</div>
            {emp.designation && <div style={{ fontSize:'0.72rem', color:'#1d4ed8', fontWeight:600, marginTop:2 }}>{emp.designation}</div>}
            {emp.department && <div style={{ fontSize:'0.68rem', color:'#64748b', marginTop:2 }}>{emp.department}</div>}
            {emp.employee_code && <div style={{ fontSize:'0.63rem', color:'#94a3b8', marginTop:3, fontFamily:'monospace' }}>{emp.employee_code}</div>}
          </div>
        </div>

        {/* Reporting chain upward */}
        {ancestors.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:'0.65rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
              Reports To ({ancestors.length} level{ancestors.length>1?'s':''})
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {ancestors.map((anc, i) => (
                <div key={anc.id}>
                  {i > 0 && (
                    <div style={{ display:'flex', justifyContent:'center', margin:'2px 0' }}>
                      <div style={{ width:2, height:10, background:'#cbd5e1' }} />
                    </div>
                  )}
                  <EmpPill emp={anc} role={i === ancestors.length - 1 ? 'Top of chain' : undefined} />
                </div>
              ))}
            </div>
          </div>
        )}
        {ancestors.length === 0 && (
          <div style={{ marginBottom:16, padding:'8px 12px', background:'#f0fdf4', borderRadius:8, fontSize:'0.75rem', color:'#16a34a', fontWeight:500 }}>
            Top-level — no one above
          </div>
        )}

        {/* Direct reports */}
        <div>
          <div style={{ fontSize:'0.65rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
            Direct Reports ({directReports.length})
          </div>
          {directReports.length === 0 ? (
            <div style={{ padding:'8px 12px', background:'#f8fafc', borderRadius:8, fontSize:'0.75rem', color:'#94a3b8' }}>No direct reports</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {directReports.map(dr => <EmpPill key={dr.id} emp={dr} />)}
            </div>
          )}
        </div>
      </div>
    );
  }

  function OrgNode({ emp, depth = 0 }) {
    const kids = children[emp.id] || [];
    const isHighlighted = highlight === emp.id;
    const hue = ((`${emp.first_name}`).charCodeAt(0) || 65) * 17 % 360;

    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', position:'relative' }}>
        {/* Card */}
        <div
          onClick={() => { setHighlight(emp.id); setSelected(emp); }}
          style={{
            background: isHighlighted ? '#eff6ff' : '#fff',
            border: `2px solid ${isHighlighted ? '#1d4ed8' : '#e2e8f0'}`,
            borderRadius: 12,
            padding: '12px 16px',
            minWidth: 160,
            maxWidth: 200,
            cursor: 'pointer',
            boxShadow: isHighlighted ? '0 0 0 3px #bfdbfe' : '0 1px 4px rgba(0,0,0,0.07)',
            transition: 'all 0.15s',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Status dot */}
          <div style={{
            position:'absolute', top:10, right:10,
            width:8, height:8, borderRadius:'50%',
            background: statusDot[emp.status] || '#6b7280',
          }} title={emp.status} />

          {/* Avatar */}
          <div style={{
            width:42, height:42, borderRadius:'50%', margin:'0 auto 8px',
            background:`hsl(${hue},50%,88%)`, color:`hsl(${hue},50%,28%)`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontWeight:700, fontSize:'1rem',
          }}>
            {`${emp.first_name[0]}${emp.last_name?.[0]||''}`.toUpperCase()}
          </div>

          <div style={{ fontWeight:700, fontSize:'0.82rem', color:'#0f172a', lineHeight:1.3 }}>
            {emp.first_name} {emp.last_name || ''}
          </div>
          {emp.designation && (
            <div style={{ fontSize:'0.7rem', color:'#1d4ed8', fontWeight:600, marginTop:2 }}>
              {emp.designation}
            </div>
          )}
          {emp.department && (
            <div style={{ fontSize:'0.68rem', color:'#64748b', marginTop:2 }}>
              {emp.department}
            </div>
          )}
          {emp.employee_code && (
            <div style={{ fontSize:'0.63rem', color:'#94a3b8', marginTop:3, fontFamily:'monospace' }}>
              {emp.employee_code}
            </div>
          )}
        </div>

        {/* Children */}
        {kids.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'100%' }}>
            {/* Vertical line down from parent */}
            <div style={{ width:2, height:24, background:'#cbd5e1' }} />

            {kids.length === 1 ? (
              <OrgNode emp={kids[0]} depth={depth+1} />
            ) : (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:'100%' }}>
                {/* Horizontal bar */}
                <div style={{ position:'relative', display:'flex', justifyContent:'center', width:'100%' }}>
                  <div style={{
                    position:'absolute', top:0, left:`calc(50% - ${(kids.length-1)*116}px)`,
                    width:`${(kids.length-1)*232}px`, height:2, background:'#cbd5e1',
                  }} />
                  {/* Vertical ticks down to each child */}
                  {kids.map((_, i) => (
                    <div key={i} style={{
                      position:'absolute', top:0,
                      left:`calc(50% + ${(i - (kids.length-1)/2) * 232}px - 1px)`,
                      width:2, height:20, background:'#cbd5e1',
                    }} />
                  ))}
                </div>
                <div style={{ height:20 }} />
                {/* Children row */}
                <div style={{ display:'flex', alignItems:'flex-start', gap:32, flexWrap:'nowrap' }}>
                  {kids.map(kid => (
                    <OrgNode key={kid.id} emp={kid} depth={depth+1} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display:'flex', gap:24, alignItems:'flex-start', padding:'24px 32px' }}>
      {/* Chart area */}
      <div style={{ flex:1, overflowX:'auto', overflowY:'auto', minHeight:200 }}>
        <div style={{ display:'flex', gap:64, alignItems:'flex-start', width:'max-content', margin:'0 auto' }}>
          {roots.map(r => (
            <OrgNode key={r.id} emp={r} />
          ))}
        </div>
        <div style={{ marginTop:24, display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap' }}>
          {Object.entries(statusDot).map(([s, c]) => (
            <div key={s} style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.72rem', color:'#64748b' }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:c }} />
              {s.replace('_',' ')}
            </div>
          ))}
        </div>
      </div>
      {/* Hierarchy panel */}
      {selected && <HierarchyPanel emp={selected} />}
    </div>
  );
}

/* ── Info row for view modal ────────────────────────────────────── */
function InfoRow({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, gap:12 }}>
      <span style={{ fontSize:'0.72rem', fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.04em', whiteSpace:'nowrap' }}>{label}</span>
      <span style={{ fontSize:'0.83rem', color:'#374151', textAlign:'right' }}>{fmt(value)}</span>
    </div>
  );
}
